import { LinkedInTopic } from '../../types/linkedin';

const VIDEO_URL = 'https://www.youtube.com/watch?v=cYQLa5GURM0';

// Función helper para generar link con timestamp
const getVideoLink = (seconds?: number) => {
  if (!seconds) return VIDEO_URL;
  
  // Formatear timestamp en formato legible (1h2m3s, 2m3s, o 3s)
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  let timestamp = '';
  if (hours > 0) {
    timestamp = `${hours}h${minutes}m${secs}s`;
  } else if (minutes > 0) {
    timestamp = `${minutes}m${secs}s`;
  } else {
    timestamp = `${secs}s`;
  }
  
  return `${VIDEO_URL}&t=${timestamp}`;
};

export const linkedInTopics: LinkedInTopic[] = [
  {
    id: 0,
    level: 'FUNDAMENTOS',
    title: 'LinkedIn como Sistema Automatizado',
    description: 'Entendé LinkedIn como un sistema diseñado para optimizar la obtención de trabajo, no solo una red social.',
    difficulty: 'beginner',
    videoUrl: 'https://www.youtube.com/watch?v=cYQLa5GURM0',
    content: {
      overview: `LinkedIn puede transformarse en un sistema automatizado que trabaja continuamente para generar oportunidades laborales. A diferencia de verlo como una red social pasiva, cuando lo optimizás correctamente, funciona como un motor que atrae reclutadores y oportunidades hacia vos, incluso cuando no estás buscando activamente.

La clave está en entender que LinkedIn utiliza algoritmos y herramientas específicas diseñadas para conectar profesionales con oportunidades. Cuando tu perfil está optimizado con las palabras clave correctas, una estructura clara y contenido estratégico, el sistema trabaja 24/7 en tu favor.

- Impacto real: El 90% de los trabajos se obtienen a través de LinkedIn o recomendaciones generadas en la plataforma. Esta estadística refleja el poder transformador de tener un perfil estratégicamente optimizado.`,
      why: `¿Por qué ver LinkedIn como sistema automatizado?

Porque cambia completamente tu enfoque y resultados. Si lo ves solo como red social, consumís contenido pasivamente. Si lo ves como sistema, lo hacés trabajar para vos.

Ventajas clave:
- Trabaja mientras dormís: Un perfil optimizado genera oportunidades sin que estés activamente buscando
- Escala tus oportunidades: Multiplica tus chances de ser encontrado por reclutadores
- Automatiza el proceso: Los algoritmos hacen el matching por vos, conectándote con oportunidades relevantes
- Diferencia competitiva: En un mercado donde miles buscan las mismas posiciones, un perfil optimizado te destaca

El problema: La mayoría trata LinkedIn como Instagram o Facebook, publicando contenido sin estrategia. El resultado: 0 ofertas, 0 contactos de reclutadores.`,
      how: `¿Cómo convertir LinkedIn en un sistema automatizado?

1. Optimización estratégica del perfil:
- Título con keywords top demandadas de tu industria
- "Sobre mí" que comunica valor claramente
- Experiencias que destacan logros medibles
- Habilidades alineadas con demandas del mercado

2. Networking algorítmico:
- Conectá con reclutadores, directores y profesionales de tu área objetivo
- Más nodos en tu red = mayor visibilidad algorítmica
- El sistema te asocia con perfiles similares, aumentando tu exposición

3. Palabras clave estratégicas:
- Investigá qué keywords buscan los reclutadores en tu rol
- Incluí tecnologías, herramientas y habilidades top demandadas
- Actualizá según tendencias del mercado

4. Consistencia y claridad:
- Un solo rol bien definido (no múltiples roles confusos)
- Mensaje claro sobre qué valor aportás
- Perfil que se refuerza en cada sección (título, about, experiencia)

Resultado: Tu perfil se convierte en un imán que atrae oportunidades automáticamente, trabajando para vos las 24 horas del día.`,
      keyConcepts: [
        {
          name: 'LinkedIn como Sistema vs Red Social',
          description: 'La diferencia fundamental está en cómo lo usás. Si lo ves como red social, solo consumís contenido. Si lo ves como sistema, lo hacés trabajar para vos.',
          characteristics: [
            'Sistema automatizado que genera atención automáticamente',
            'Optimizado para búsqueda de trabajo',
            'Trabaja 24/7 mientras dormís',
            'Genera atención y trae reclutadores',
            'Escala tus oportunidades de conseguir trabajo',
            'Analogía: LinkedIn como red de pesca (dejás la red y los peces caen)'
          ],
          examples: [
            'Si nos imaginamos LinkedIn como una caña de pescar, podríamos decir que podemos agarrar una media red y dejarla colgada esperando que los peces caigan',
            'Sistema que automatiza el proceso de búsqueda',
            'Plataforma inteligente que conecta oferta y demanda'
          ]
        },
        {
          name: 'Ventajas Competitivas del Sistema',
          description: 'LinkedIn ofrece herramientas específicas que automatizan gran parte del proceso de búsqueda de oportunidades laborales.',
          characteristics: [
            'Algoritmos de matching por palabras clave',
            'Visibilidad automática a reclutadores',
            'Alertas personalizadas de vacantes',
            'Networking inteligente con sugerencias',
            'Análisis de tendencias del mercado laboral'
          ],
          examples: [
            'Alertas semanales de aumento de demanda en tu zona',
            'Sugerencias de contactos relevantes',
            'Búsquedas automáticas por keywords'
          ]
        },
        {
          name: 'El Mercado de Trabajo en LinkedIn',
          description: 'LinkedIn es como un mercado donde las personas muestran sus conocimientos y los reclutadores eligen. La forma en que comunicás tu valor marca la diferencia.',
          characteristics: [
            'Mercado competitivo: 10,000 personas vs 100 vacantes',
            'Importa tanto lo que sabés como cómo lo comunicás',
            'Perfil optimizado = más chances de ser elegido',
            'Comunicar valor es tan importante como tenerlo',
            'Diferencia entre perfil abandonado vs optimizado'
          ],
          examples: [
            'Personas con más conocimiento pero menos oportunidades por no comunicar bien',
            'Perfiles optimizados que generan ofertas constantemente',
            'La diferencia está en cómo presentás tu valor'
          ]
        }
      ],
      bestPractices: [
        'Ver LinkedIn como sistema, no solo como red social',
        'Optimizar el perfil para que trabaje automáticamente',
        'Entender que es tu herramienta principal de búsqueda de trabajo',
        'Aprovechar las herramientas automatizadas (alertas, matching, networking)',
        'Medir el éxito por oportunidades generadas, no por likes'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Antes y Después de Optimizar',
        description: 'Comparación de resultados entre perfil abandonado vs optimizado',
        scenario: `Perfil Abandonado:
- Sin keywords estratégicas
- Título genérico o ambiguo
- "Sobre mí" vacío o desactualizado
- Pocos contactos relevantes
Resultado: 0 ofertas, 0 contactos de reclutadores

Perfil Optimizado:
- Keywords top demandadas en título
- Título específico y claro
- "Sobre mí" que comunica valor
- Red de contactos estratégica
Resultado: Ofertas constantes, reclutadores te contactan primero`,
        solution: `La diferencia está en ver LinkedIn como sistema automatizado. Un perfil optimizado trabaja 24/7, generando oportunidades mientras dormís. El 90% de trabajos se obtienen por LinkedIn cuando el perfil está correctamente optimizado.`
      }
    ]
  },
  {
    id: 1,
    level: 'FUNDAMENTOS',
    title: '¿Por qué LinkedIn? Fundamentos',
    description: 'Comprendé por qué LinkedIn es la plataforma más efectiva para conseguir trabajo y cómo aprovechar sus ventajas competitivas.',
    difficulty: 'beginner',
    content: {
      overview: `LinkedIn fue diseñado específicamente para optimizar la obtención de trabajo. A diferencia de otras redes sociales como Instagram o TikTok, LinkedIn tiene características únicas que lo convierten en la herramienta más poderosa para tu carrera profesional.

Cada red social tiene su propósito específico. LinkedIn no está diseñado para consumo pasivo de contenido, sino para generar oportunidades profesionales que te acerquen a tus objetivos de carrera.

En un mercado laboral competitivo donde miles de profesionales buscan las mismas posiciones, tener un perfil estratégicamente optimizado marca la diferencia entre ser encontrado por reclutadores o pasar desapercibido.`,
      why: `¿Por qué LinkedIn es la plataforma más efectiva?

Porque fue diseñado desde cero con un propósito específico: conectar profesionales con oportunidades laborales. No es una red social adaptada, es una herramienta profesional especializada.

Ventajas únicas:
- Algoritmos especializados: Diseñados específicamente para matching profesional, no para engagement social
- Herramientas automatizadas: Alertas, sugerencias y búsquedas que trabajan por vos
- Enfoque en resultados: La métrica de éxito es oportunidades generadas, no likes o comentarios
- Red profesional: Contactos estratégicos que amplifican tu visibilidad

El problema con otras redes: Instagram, TikTok y Facebook están optimizados para entretenimiento y engagement, no para conectar profesionales con oportunidades.`,
      how: `¿Cómo aprovechar LinkedIn efectivamente?

1. Cambiar el mindset:
- De "red social" a "sistema de oportunidades"
- De "consumir contenido" a "generar oportunidades"
- De "publicar por likes" a "optimizar para resultados"

2. Usar herramientas específicas:
- Activar alertas personalizadas de vacantes
- Configurar búsquedas guardadas con keywords relevantes
- Aprovechar sugerencias de networking inteligente
- Monitorear tendencias del mercado laboral

3. Medir resultados correctos:
- Ofertas recibidas (no likes)
- Contactos de reclutadores (no seguidores)
- Entrevistas generadas (no comentarios)
- Oportunidades concretas (no engagement)

Resultado: LinkedIn se convierte en tu herramienta principal de búsqueda de trabajo, trabajando 24/7 en tu favor.`,
      keyConcepts: [
        {
          name: 'Diseño Específico para Trabajo',
          description: 'LinkedIn fue creado con un propósito claro: conectar profesionales con oportunidades laborales de forma eficiente.',
          characteristics: [
            'Plataforma diseñada específicamente para búsqueda de trabajo',
            'No es una red social genérica como Instagram o TikTok',
            'Herramientas específicas para matching y búsqueda',
            'Algoritmos optimizados para conectar oferta y demanda',
            'Enfoque en resultados profesionales, no entretenimiento'
          ],
          examples: [
            'Sistemas de búsqueda por keywords específicas',
            'Alertas personalizadas de vacantes',
            'Networking inteligente con sugerencias relevantes'
          ]
        },
        {
          name: 'Ventajas Competitivas Únicas',
          description: 'LinkedIn ofrece herramientas que ninguna otra plataforma tiene para búsqueda de trabajo.',
          characteristics: [
            'Algoritmos de matching automático',
            'Visibilidad directa a reclutadores',
            'Alertas de tendencias del mercado laboral',
            'Networking algorítmico inteligente',
            'Análisis de demanda de tu rol en tiempo real'
          ],
          examples: [
            'Alertas: "Aumentó 20% la búsqueda de ingenieros de datos en tu zona"',
            'Sugerencias de contactos relevantes automáticas',
            'Búsquedas que te encuentran a vos, no al revés'
          ]
        },
        {
          name: 'El Factor Competitivo',
          description: 'En un mercado competitivo, tener LinkedIn optimizado marca la diferencia entre conseguir trabajo o quedarte afuera.',
          characteristics: [
            'Mercado competitivo: muchos candidatos, pocas vacantes',
            'Los que optimizan su perfil tienen ventaja clara',
            'Comunicar valor es tan importante como tenerlo',
            'Perfil optimizado = más oportunidades',
            'Diferencia entre ser encontrado vs buscar activamente'
          ],
          examples: [
            'Escenario: 10,000 personas buscando vs 100 vacantes',
            'Los perfiles optimizados son los que consiguen trabajo',
            'Comunicar valor efectivamente marca la diferencia'
          ]
        }
      ],
      bestPractices: [
        'Usar LinkedIn como herramienta principal de búsqueda de trabajo',
        'Aprovechar las herramientas automatizadas únicas de LinkedIn',
        'Entender que es un mercado competitivo donde la optimización marca la diferencia',
        'Ver LinkedIn como inversión en tu carrera, no como red social',
        'Medir resultados por oportunidades generadas, no por engagement'
      ]
    }
  },
  {
    id: 2,
    level: 'FUNDAMENTOS',
    title: 'Algoritmos de Matching y Búsqueda',
    description: 'Comprendé cómo LinkedIn usa algoritmos para conectar tu perfil con reclutadores que buscan exactamente lo que vos tenés.',
    difficulty: 'beginner',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 914, // "ejemplo, acá data engineer, asure datab SQL Data Factory. Yo en esta búsqueda"
    content: {
      overview: `LinkedIn utiliza algoritmos de matching para conectar automáticamente tu perfil con reclutadores que buscan exactamente lo que ofrecés. El sistema funciona como un filtro inteligente que muestra solo los perfiles relevantes basándose en palabras clave específicas.

Cuando un reclutador realiza una búsqueda, LinkedIn analiza millones de perfiles y presenta únicamente aquellos que contienen las palabras clave buscadas. Los perfiles sin esas keywords quedan completamente fuera de los resultados, independientemente de la experiencia real del candidato.

Este sistema de filtrado automático significa que tu visibilidad depende directamente de las palabras clave que incluyas estratégicamente en tu perfil. Sin ellas, quedás invisible para los reclutadores, incluso si tenés la experiencia perfecta.`,
      why: `¿Por qué los algoritmos de matching son críticos?

Porque determinan si aparecés o no en las búsquedas de reclutadores. Sin las palabras clave correctas, tu perfil es invisible, sin importar cuán calificado estés.

El problema real:
- Reclutadores buscan con keywords específicas (ej: "Databricks", "Python", "Azure")
- LinkedIn filtra automáticamente perfiles sin esas keywords
- Si no las tenés, quedás fuera, aunque sepas usar esas tecnologías
- La demanda de tecnologías cambia constantemente (ej: Databricks creció 17-20% en publicaciones)

Impacto: Perder oportunidades no por falta de habilidades, sino por falta de keywords estratégicas en tu perfil.`,
      how: `¿Cómo optimizar para algoritmos de matching?

1. Investigar keywords top demandadas:
- Analizar publicaciones de trabajo en tu área
- Identificar tecnologías, herramientas y habilidades más mencionadas
- Priorizar keywords con alta demanda y poca competencia

2. Incluir keywords estratégicamente:
- Título: Incluir 3-5 keywords principales
- "Sobre mí": Mencionar keywords en contexto natural
- Experiencia: Usar keywords en descripciones de roles
- Habilidades: Agregar todas las keywords relevantes

3. Actualizar según tendencias:
- Monitorear alertas de LinkedIn sobre demanda
- Agregar nuevas keywords cuando aumentan en el mercado
- Remover keywords obsoletas o poco demandadas

4. Balancear keywords con claridad:
- No saturar con keywords (parece spam)
- Integrar naturalmente en el contenido
- Mantener mensaje claro y profesional

Resultado: Tu perfil aparece en búsquedas relevantes, aumentando exponencialmente tu visibilidad ante reclutadores.`,
      keyConcepts: [
        {
          name: 'Búsqueda por Palabras Clave',
          description: 'Los reclutadores buscan usando palabras clave específicas. Si no las tenés en tu perfil, no aparecés en los resultados.',
          characteristics: [
            'Búsquedas automáticas por keywords',
            'Filtrado automático de perfiles',
            'Solo aparecen perfiles con las palabras clave',
            'Case insensitive (mayúsculas/minúsculas no importan)',
            'Las palabras clave deben estar en el perfil para aparecer'
          ],
          examples: [
            'Búsqueda: "Data Engineer, Databricks, SQL"',
            'Resultado: Solo perfiles con esas keywords',
            'Los que no las tienen quedan fuera automáticamente'
          ]
        },
        {
          name: 'Visibilidad a Reclutadores',
          description: 'El algoritmo permite que los reclutadores te encuentren fácilmente cuando tu perfil tiene las palabras clave que buscan.',
          characteristics: [
            'Filtrado automático por keywords',
            'Visibilidad directa en búsquedas',
            'Mayor visibilidad = más oportunidades',
            'Keywords estratégicas = más apariciones',
            'Sin keywords = invisible para reclutadores'
          ],
          examples: [
            'Reclutador busca "Databricks" → Solo ve perfiles con Databricks',
            'Perfiles sin keywords quedan fuera',
            'Keywords top demandadas = más visibilidad'
          ]
        },
        {
          name: 'Tecnologías en Demanda',
          description: 'Algunas tecnologías tienen más demanda que otras. Incluir las top demandadas aumenta significativamente tu visibilidad.',
          characteristics: [
            'Python: Mayor demanda en datos',
            'SQL: Segunda más demandada',
            'Databricks: 17-20% de publicaciones (creciendo)',
            'Azure, AWS, GCP: Nubes más demandadas',
            'Tecnologías obsoletas = menos oportunidades'
          ],
          examples: [
            'Top demandadas: Python, SQL, Databricks, Azure, AWS',
            'Menos demandadas: Java, Hadoop (tecnologías antiguas)',
            'Incluir top demandadas = más oportunidades'
          ]
        }
      ],
      bestPractices: [
        'Incluir keywords top demandadas en tu perfil',
        'Actualizar keywords según tendencias del mercado',
        'Evitar tecnologías obsoletas o poco demandadas',
        'Usar keywords específicas, no genéricas',
        'Revisar qué keywords buscan los reclutadores en tu área'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Búsqueda Real de Reclutador',
        description: 'Ejemplo de cómo un reclutador busca y qué perfiles aparecen',
        scenario: `Búsqueda del Reclutador:
"Data Engineer, Azure, Databricks, SQL"

Perfiles que Aparecen:
1. ✅ "Senior Data Engineer, Azure, Databricks, SQL, Python" → Aparece
2. ✅ "Data Engineer, Databricks, SQL" → Aparece
3. ❌ "Data Engineer, Maestría en Data Science" → No aparece (falta keywords)
4. ❌ "Data Manager, Data Engineer, Data Architect" → No aparece (confuso, no específico)

Resultado: El reclutador solo ve los perfiles 1 y 2. Los demás quedan fuera automáticamente.`,
        solution: `Incluir keywords específicas y top demandadas en tu perfil es crítico. Sin ellas, quedás invisible para los reclutadores, incluso si sabés usar esas tecnologías. El algoritmo no puede adivinar lo que sabés si no lo ponés en tu perfil.`
      }
    ]
  },
  {
    id: 3,
    level: 'FUNDAMENTOS',
    title: 'Alertas Personalizadas y Tendencias del Mercado',
    description: 'Aprovechá las alertas de LinkedIn para entender las tendencias del mercado laboral y ajustar tu estrategia.',
    difficulty: 'beginner',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 522, // "después también está la otra debe recibir alertas personalizadas con"
    content: {
      overview: `LinkedIn ofrece un sistema de alertas personalizadas que te notifica automáticamente cuando aparecen oportunidades laborales relevantes basadas en tu perfil actual, tu título y tus roles anteriores.

El sistema analiza continuamente el mercado laboral y te proporciona insights valiosos sobre las tendencias de demanda de tu área profesional. Estas alertas te permiten entender si tu rol está en crecimiento o declive, información crítica para tomar decisiones estratégicas sobre tu carrera.

Más allá de simplemente informarte sobre nuevas vacantes, estas alertas funcionan como un barómetro del mercado laboral, ayudándote a ajustar tu estrategia de búsqueda y optimización de perfil según las tendencias actuales.`,
      why: `¿Por qué las alertas personalizadas son valiosas?

Porque te ahorran tiempo y te dan información estratégica sobre el mercado. En lugar de buscar activamente, el sistema te trae oportunidades relevantes y datos sobre tendencias.

Ventajas clave:
- Ahorro de tiempo: No necesitás buscar manualmente, las oportunidades llegan a vos
- Información estratégica: Sabés si tu rol está en demanda o declive
- Alertas proactivas: Te enterás de nuevas oportunidades antes que otros
- Datos del mercado: Información para tomar decisiones informadas sobre tu carrera

El problema sin alertas: Buscar manualmente es ineficiente y no tenés visibilidad de tendencias del mercado que podrían impactar tu carrera.`,
      how: `¿Cómo usar alertas personalizadas estratégicamente?

1. Configurar alertas correctamente:
- Basar alertas en tu título actual y roles anteriores
- Incluir keywords relevantes en las búsquedas guardadas
- Configurar ubicación y tipo de trabajo preferido
- Ajustar frecuencia de notificaciones según necesidad

2. Analizar tendencias del mercado:
- Revisar alertas semanales de aumento/disminución de demanda
- Identificar patrones en tu zona geográfica
- Usar datos para ajustar estrategia de carrera
- Considerar nuevas habilidades si la demanda disminuye

3. Actuar según la información:
- Si aumenta demanda: optimizar más el perfil
- Si disminuye demanda: considerar nuevas habilidades o especializaciones
- Usar alertas para ajustar keywords en tu perfil
- Planificar cambios estratégicos basados en datos

Resultado: Tenés visibilidad constante del mercado y oportunidades que llegan automáticamente, permitiéndote tomar decisiones informadas sobre tu carrera.`,
      keyConcepts: [
        {
          name: 'Alertas Personalizadas de Vacantes',
          description: 'LinkedIn te avisa automáticamente cuando aparecen puestos relevantes basados en tu perfil y rol actual.',
          characteristics: [
            'Alertas basadas en tu titular actual',
            'Alertas basadas en tu rol en trabajos anteriores',
            'Notificaciones automáticas de nuevas oportunidades',
            'Filtrado inteligente según tu perfil',
            'Ahorra tiempo en búsqueda activa'
          ],
          examples: [
            'Alertas semanales de nuevos puestos en tu área',
            'Notificaciones de oportunidades que coinciden con tu perfil',
            'Sugerencias de trabajos relevantes automáticas'
          ]
        },
        {
          name: 'Análisis de Tendencias del Mercado',
          description: 'LinkedIn te permite entender si la demanda de tu rol está aumentando o disminuyendo en tu zona.',
          characteristics: [
            'Alertas de aumento/disminución de demanda',
            'Análisis por zona geográfica',
            'Tendencias semanales del mercado',
            'Datos para ajustar estrategia de carrera',
            'Información para tomar decisiones informadas'
          ],
          examples: [
            '"Aumentó un 20% en tu zona la búsqueda de ingenieros de datos"',
            'Alertas de disminución de demanda para considerar estrategias',
            'Tendencias que ayudan a planificar tu carrera'
          ]
        },
        {
          name: 'Uso Estratégico de Alertas',
          description: 'Las alertas no solo te informan, también te ayudan a ajustar tu estrategia de búsqueda y perfil.',
          characteristics: [
            'Identificar si tu rol está en demanda',
            'Ajustar keywords según tendencias',
            'Planificar cambios de carrera si es necesario',
            'Optimizar perfil según demanda del mercado',
            'Tomar decisiones informadas sobre tu carrera'
          ],
          examples: [
            'Si aumenta demanda: optimizar más el perfil',
            'Si disminuye demanda: considerar nuevas habilidades',
            'Usar alertas para ajustar estrategia de búsqueda'
          ]
        }
      ],
      bestPractices: [
        'Revisar alertas semanales regularmente',
        'Usar alertas para entender tendencias del mercado',
        'Ajustar perfil según aumentos/disminuciones de demanda',
        'No ignorar alertas de disminución de demanda',
        'Usar datos para tomar decisiones estratégicas de carrera'
      ]
    }
  },
  {
    id: 4,
    level: 'FUNDAMENTOS',
    title: 'Competitividad: El Mercado de Trabajo',
    description: 'Entendé cómo funciona el mercado competitivo de LinkedIn y por qué optimizar tu perfil marca la diferencia.',
    difficulty: 'beginner',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 601, // "ver, yo siempre digo que es como un un mercado de de puestos de trabajo y roles"
    content: {
      overview: `LinkedIn funciona como un mercado competitivo donde miles de profesionales compiten por un número limitado de oportunidades. En este escenario, la optimización de tu perfil marca la diferencia entre ser elegido o pasar desapercibido.

La realidad del mercado laboral digital es clara: cuando 10,000 personas buscan las mismas 100 vacantes, solo aquellos con perfiles estratégicamente optimizados y que comunican valor efectivamente tienen chances reales de éxito.

Este mercado funciona como cualquier otro: las personas muestran sus conocimientos y habilidades, y los reclutadores eligen. Sin embargo, la diferencia crítica está en cómo presentás y comunicás tu valor. Un perfil optimizado no solo muestra lo que sabés, sino que lo comunica de manera que los reclutadores lo encuentren y lo valoren.`,
      why: `¿Por qué la competitividad importa?

Porque LinkedIn es un mercado con alta competencia. Sin optimización, quedás invisible entre miles de candidatos.

La realidad del mercado:
- Alta competencia: Miles de candidatos para pocas vacantes
- Filtrado automático: Solo los perfiles optimizados aparecen en búsquedas
- Primera impresión crítica: Reclutadores deciden en segundos
- Comunicar valor es clave: No basta con tener habilidades, hay que mostrarlas

El problema: Muchos profesionales tienen las habilidades pero no las comunican efectivamente, perdiendo oportunidades frente a quienes sí lo hacen.`,
      how: `¿Cómo competir efectivamente en el mercado?

1. Optimizar para visibilidad:
- Incluir keywords estratégicas en título y perfil
- Estructurar perfil para que sea fácil de escanear
- Destacar logros medibles, no solo responsabilidades
- Usar lenguaje que comunique valor, no solo tareas

2. Comunicar valor claramente:
- Enfocarse en resultados e impacto, no solo en procesos
- Usar números y métricas cuando sea posible
- Mostrar cómo tu trabajo genera valor para el negocio
- Evitar jerga técnica excesiva, usar lenguaje accesible

3. Preparar para ser encontrado:
- Optimizar perfil para búsquedas de reclutadores
- Asegurar que keywords relevantes estén presentes
- Mantener perfil actualizado y profesional
- Responder rápidamente a contactos de reclutadores

Resultado: Tu perfil compite efectivamente, destacando entre miles de candidatos y generando oportunidades consistentemente.`,
      keyConcepts: [
        {
          name: 'El Mercado Competitivo',
          description: 'LinkedIn es un mercado donde muchos candidatos compiten por pocas vacantes. La optimización marca la diferencia.',
          characteristics: [
            'Escenario: 10,000 personas buscando vs 100 vacantes',
            'Competencia alta por cada posición',
            'Los perfiles optimizados tienen ventaja clara',
            'Importa tanto lo que sabés como cómo lo comunicás',
            'Diferencia entre perfil abandonado vs optimizado'
          ],
          examples: [
            'Mercado donde las personas muestran conocimientos',
            'Reclutadores eligen entre muchos candidatos',
            'La forma de comunicar valor marca la diferencia'
          ]
        },
        {
          name: 'Comunicar Valor vs Tener Conocimiento',
          description: 'Mucha gente sabe más que otros, pero no logra tener los mismos sueldos o las mismas oportunidades por una falta de comunicar su valor.',
          characteristics: [
            'Conocimiento sin comunicación = menos oportunidades',
            'Comunicar valor efectivamente = más oportunidades',
            'Importa tanto lo que sabés como cómo lo mostrás',
            'Perfil optimizado comunica valor automáticamente',
            'Diferencia entre saber y hacer saber'
          ],
          examples: [
            'Personas con más conocimiento pero menos oportunidades',
            'Comunicar valor es tan importante como tenerlo',
            'El perfil optimizado comunica valor sin esfuerzo'
          ]
        },
        {
          name: 'Preparación para Ser Encontrado',
          description: 'La diferencia está en tener tu perfil preparado para que te encuentren, no solo buscar activamente.',
          characteristics: [
            'Perfil preparado = reclutadores te encuentran',
            'Optimización = más visibilidad automática',
            'Comunicar valor = más oportunidades',
            'Ser encontrado vs buscar activamente',
            'Perfil que trabaja 24/7 por vos'
          ],
          examples: [
            'Perfiles optimizados que generan ofertas constantemente',
            'Reclutadores contactan primero a perfiles optimizados',
            'Diferencia entre buscar trabajo vs que el trabajo te busque'
          ]
        }
      ],
      bestPractices: [
        'Optimizar perfil para competir efectivamente',
        'Comunicar valor, no solo mostrar conocimiento',
        'Preparar perfil para ser encontrado, no solo buscar',
        'Entender que es un mercado competitivo',
        'Invertir tiempo en optimización para mejores resultados'
      ]
    }
  },
  {
    id: 5,
    level: 'FUNDAMENTOS',
    title: 'Networking Algorítmico',
    description: 'Aprendé cómo funciona el networking algorítmico de LinkedIn y cómo usar los nodos de contactos para aumentar tu visibilidad.',
    difficulty: 'beginner',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 109, // "poco cómo es el networking, digamos algorítmico. La idea de todo esto es"
    content: {
      overview: `El networking algorítmico en LinkedIn funciona como una red de conexiones donde cada contacto (nodo) amplifica tu visibilidad. Cuantos más contactos relevantes tengás en tu red, mayor será tu exposición a otros profesionales, incluyendo reclutadores y tomadores de decisión.

El algoritmo de LinkedIn asocia tu perfil con los perfiles de tus contactos. Cuando un reclutador busca candidatos, el sistema muestra no solo perfiles que coinciden directamente, sino también perfiles asociados a su red de contactos. Esto significa que tener contactos estratégicos aumenta exponencialmente tus chances de ser encontrado.

La estrategia no es simplemente agregar cualquier persona, sino construir una red de contactos relevantes: reclutadores de tu industria, directores de áreas relacionadas, y profesionales que trabajan en roles similares al que buscás.`,
      why: `¿Por qué el networking algorítmico es poderoso?

Porque amplifica tu visibilidad exponencialmente. Cada contacto estratégico te conecta con toda su red, multiplicando tus oportunidades.

Cómo funciona:
- Nodos en la red: Cada contacto es un nodo que te conecta con otros
- Asociación algorítmica: LinkedIn te asocia con perfiles similares a tus contactos
- Visibilidad amplificada: Más contactos relevantes = más exposición
- Conexiones indirectas: Reclutadores te encuentran a través de contactos mutuos

El problema sin networking estratégico: Un perfil aislado tiene visibilidad limitada. Sin contactos relevantes, quedás fuera de muchas oportunidades que se distribuyen a través de redes profesionales.`,
      how: `¿Cómo construir networking algorítmico efectivo?

1. Identificar contactos estratégicos:
- Reclutadores especializados en tu área
- Directores y líderes de equipos en tu industria
- Profesionales que trabajan en roles similares al que buscás
- Personas que trabajan en empresas objetivo

2. Conectar estratégicamente:
- Enviar solicitudes personalizadas explicando por qué querés conectar
- Participar en grupos y comunidades relevantes
- Comentar y compartir contenido de profesionales clave
- Asistir a eventos virtuales y conectar con participantes

3. Mantener red activa:
- Interactuar regularmente con contenido de contactos
- Compartir insights y contenido valioso
- Responder a mensajes y solicitudes profesionalmente
- Mantener perfil actualizado para que contactos te recomienden

4. Evitar errores comunes:
- No agregar contactos sin contexto (parece spam)
- No tener múltiples roles confusos (algoritmo no sabe cómo asociarte)
- No ignorar solicitudes de conexión relevantes

Resultado: Tu red trabaja como un amplificador, conectándote con oportunidades que de otra forma no encontrarías.`,
      keyConcepts: [
        {
          name: 'Concepto de Nodos en la Red',
          description: 'Cuantos más nodos (contactos) tengas en tu red, más visibilidad tendrás porque el algoritmo te asocia con más personas.',
          characteristics: [
            'Mayor cantidad de nodos = mayor visibilidad',
            'Nodos alrededor de tus contactos llegan a vos',
            'Reclutadores aparecen como nodos conectados',
            'Red más grande = más oportunidades',
            'El algoritmo asocia perfiles similares'
          ],
          examples: [
            'Contactos con reclutadores = más visibilidad',
            'Red grande = más nodos conectados',
            'Algoritmo sugiere contactos relevantes automáticamente'
          ]
        },
        {
          name: 'A Quién Agregar Estratégicamente',
          description: 'El networking algorítmico es cómo orientarnos en la gente que vamos a estar agregando, orientarnos en a quién le vamos a dirigir nuestro perfil.',
          characteristics: [
            'Agregar directores de distintas áreas',
            'Agregar reclutadores de los roles que nos interesan',
            'Agregar personas que queremos que vean nuestro perfil',
            'Agregar personas del mismo perfil objetivo',
            'Estrategia: quién queremos que lo vea, por ende quién agregamos'
          ],
          examples: [
            'Directores de Data Engineering',
            'Reclutadores especializados en tu área',
            'Personas que lograron el rol que buscás'
          ]
        },
        {
          name: 'Problema de Perfiles con Múltiples Roles',
          description: 'Cuando tenés múltiples roles en tu perfil, el algoritmo no sabe cómo asociarte y los reclutadores se confunden.',
          characteristics: [
            'Perfiles con 3 roles (Data Analyst, Data Scientist, Data Engineer) = confusión',
            'Reclutadores no saben para qué puesto aplicás',
            'Algoritmo no sabe cómo asociarte',
            'Sugerencias menos precisas',
            'Menos visibilidad en búsquedas específicas'
          ],
          examples: [
            'Caso típico: Data Analyst, Data Scientist, Data Engineer',
            'Reclutador: "¿Para qué puesto estás aplicando?"',
            'Confusión algorítmica = menos oportunidades'
          ]
        }
      ],
      bestPractices: [
        'Agregar contactos estratégicos (reclutadores, directores)',
        'Evitar múltiples roles que confundan al algoritmo',
        'Construir red de personas del mismo perfil objetivo',
        'Usar sugerencias de LinkedIn para expandir red',
        'Entender que más nodos = más visibilidad'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Caso Real: Múltiples Roles',
        description: 'Ejemplo de cómo múltiples roles confunden al algoritmo y a los reclutadores',
        scenario: `Perfil Problemático:
- Título: "Data Analyst, Data Scientist, Data Engineer"
- Múltiples roles = confusión algorítmica
- Reclutador no sabe para qué puesto aplicás
- Algoritmo no sabe cómo asociarte

Resultado:
- Menos sugerencias precisas de contactos
- Reclutadores te rechazan por ambigüedad
- Menos visibilidad en búsquedas específicas`,
        solution: `Especializate en un solo rol. Si querés ser Data Engineer, poné solo "Data Engineer" con keywords específicas. El algoritmo te asociará con otros Data Engineers y los reclutadores sabrán exactamente qué buscás.`
      }
    ]
  },
  {
    id: 6,
    level: 'INTERMEDIO',
    title: 'El Hook: Primeros 3-5 Segundos',
    description: 'Aprendé cómo captar la atención del reclutador en los primeros 3-5 segundos con un hook efectivo.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1130, // "el hook, que es lo que viene acá, lo que es la foto perfil y las primeras"
    content: {
      overview: `El hook es el elemento que capta la atención del reclutador en los primeros 3-5 segundos. En un entorno saturado de contenido, esta primera impresión determina si tu perfil será explorado en profundidad o descartado inmediatamente.

El hook en LinkedIn está compuesto por dos elementos visuales críticos: tu foto de perfil y las primeras palabras de tu título (headline). Estos elementos aparecen juntos en los resultados de búsqueda y en la vista previa de tu perfil, formando la primera impresión que un reclutador tiene de vos.

La estructura de un perfil efectivo sigue el principio de contenido: hook (3-5 segundos de atención) seguido del guión (contenido completo que desarrolla tu valor). El hook debe ser impactante, claro y específico, mientras que el guión proporciona el contexto y los detalles que refuerzan tu propuesta de valor.`,
      why: `¿Por qué el hook es crítico?

Porque los reclutadores tienen tiempo limitado y reciben cientos de perfiles. Si no captás su atención en los primeros segundos, perdés la oportunidad.

La realidad:
- Saturación de contenido: Hay demasiado contenido, la atención es escasa
- 3-5 segundos máximo: Ese es el tiempo que tenés para captar atención
- Primera impresión: Determina si el reclutador sigue leyendo o pasa al siguiente
- Competencia alta: Miles de perfiles compiten por la misma atención

- El problema sin hook efectivo: Un perfil con hook débil o confuso es ignorado inmediatamente, sin importar cuán calificado estés.`,
      how: `¿Cómo crear un hook efectivo?

1. Foto profesional:
- Buena iluminación, cara clara y visible
- Sonrisa genuina (genera afinidad)
- Vestimenta apropiada para tu industria
- Sin elementos distractores (gorras, filtros, etc.)
- Fondo neutro o profesional

2. Primeras palabras del título:
- Empezar con tu rol específico (ej: "Senior Data Engineer")
- Incluir 2-3 keywords top demandadas
- Ser claro y específico, no genérico
- Evitar ambigüedad o múltiples roles

3. Estructura del hook:
- Foto: Genera afinidad y confianza
- Título: Comunica rol y keywords inmediatamente
- Juntos: Crean primera impresión coherente y profesional

4. Testear efectividad:
- Ver cómo se ve tu perfil en resultados de búsqueda
- Preguntar a colegas su primera impresión
- Ajustar según feedback

Resultado: Tu hook capta atención inmediatamente, haciendo que los reclutadores quieran leer más sobre vos.`,
      keyConcepts: [
        {
          name: 'El Hook: Foto + Primeras Palabras',
          description: 'El hook es lo primero que ve el reclutador: la foto del perfil y las primeras palabras del título. Esto determina si siguen leyendo o no.',
          characteristics: [
            'Hook = Foto del perfil + primeras palabras',
            '3-5 segundos máximo de atención',
            'Determina si el reclutador sigue leyendo',
            'Primera impresión es crítica',
            'Comparable con contenido de TikTok/LinkedIn'
          ],
          examples: [
            'Foto profesional + título con keywords = hook efectivo',
            'Foto borrosa + título genérico = hook débil',
            'Primeras palabras del perfil son el gancho'
          ]
        },
        {
          name: 'Todo se Trata de Atención',
          description: 'En las redes hay tanto contenido que solo tenés 3-5 segundos para captar la atención. Después de eso, el reclutador sigue al siguiente perfil.',
          characteristics: [
            '3-5 segundos máximo de atención',
            'Hay tanto contenido que la atención es limitada',
            'Si no captás atención en ese tiempo, perdés la oportunidad',
            'El hook debe ser impactante y claro',
            'Debe encajar con lo que el reclutador busca'
          ],
          examples: [
            'Comparación con TikTok: primeros segundos determinan si siguen viendo',
            'LinkedIn funciona igual: primeros segundos determinan si leen tu perfil',
            'Atención limitada = hook crítico'
          ]
        },
        {
          name: 'Hook vs Guión',
          description: 'El perfil tiene dos partes: el hook (3-5 seg) que capta atención, y el guión (resto del perfil) que desarrolla tu valor.',
          characteristics: [
            'Hook: 3-5 segundos, algo impactante y llamativo',
            'Hook: Debe encajar con lo que el reclutador busca',
            'Guión: Experiencia, proyectos, recomendaciones, habilidades',
            'Guión: Desarrolla el valor después de captar atención',
            'Ambos son necesarios: hook sin guión = oportunidad perdida'
          ],
          examples: [
            'Hook efectivo + guión débil = reclutador se va',
            'Hook débil = reclutador nunca lee el guión',
            'Hook + guión efectivos = oportunidad completa'
          ]
        }
      ],
      bestPractices: [
        'Optimizar foto profesional para el hook',
        'Usar primeras palabras del título como gancho',
        'Incluir keywords top demandadas en el hook',
        'Hacer el hook específico y claro',
        'Asegurarse de que el hook encaje con lo que buscan los reclutadores'
      ]
    }
  },
  {
    id: 7,
    level: 'INTERMEDIO',
    title: 'Títulos que Convierten (Headline)',
    description: 'Aprendé cómo crear títulos específicos y orientados a un rol particular que conviertan y atraigan reclutadores.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 45, // "principales que van a ser títulos que convierten. Esto es siempre orientado"
    content: {
      overview: `El título (headline) de tu perfil es uno de los elementos más críticos para la conversión. Un título efectivo comunica inmediatamente tu rol específico y las tecnologías clave que manejás, mientras que un título genérico o confuso hace que los reclutadores pasen de largo.

Los títulos que convierten tienen características específicas: están orientados a un solo rol (no múltiples), incluyen keywords top demandadas, y son claros y específicos. Cuando un reclutador busca candidatos, los títulos efectivos aparecen inmediatamente como relevantes, mientras que los títulos confusos o genéricos son ignorados.

La diferencia entre un título que convierte y uno que no, se ve claramente en los resultados de búsqueda. Los títulos efectivos combinan rol específico con tecnologías demandadas (ej: "Senior Data Engineer, Azure, Databricks, SQL, Python"), mientras que los inefectivos son vagos, genéricos o incluyen múltiples roles que generan confusión.`,
      why: `¿Por qué los títulos que convierten son críticos?

Porque son lo primero que ve el reclutador después de tu foto. Si el título no comunica valor inmediatamente, perdés la oportunidad.

El problema con títulos genéricos:
- Confusión: Múltiples roles hacen que el reclutador no sepa qué buscás
- Falta de keywords: Sin tecnologías específicas, no aparecés en búsquedas
- Ambigüedad: Títulos vagos no comunican valor
- Rechazo inmediato: Reclutadores pasan al siguiente perfil

- Impacto: Un título efectivo puede multiplicar tus oportunidades, mientras que uno inefectivo te hace invisible incluso si tenés la experiencia perfecta.`,
      how: `¿Cómo crear títulos que convierten?

1. Estructura efectiva:
- Rol específico primero: "Senior Data Engineer" (no múltiples roles)
- Keywords top demandadas: "Azure, Databricks, SQL, Python"
- Formato: "Rol + Tecnologías" (ej: "Senior Data Engineer, Azure, Databricks, SQL, Python")

2. Evitar errores comunes:
- ❌ Múltiples roles: "Data Manager, Data Engineer, Data Architect"
- ❌ Sin keywords: "Data Engineer, Maestría en Data Science"
- ❌ Genéricos: "Especialista en datos" o "Programación"

3. Investigar keywords relevantes:
- Analizar publicaciones de trabajo en tu área
- Identificar tecnologías más mencionadas
- Priorizar keywords con alta demanda
- Actualizar según tendencias del mercado

4. Testear efectividad:
- Ver cómo aparece tu título en resultados de búsqueda
- Comparar con títulos de perfiles exitosos
- Ajustar según feedback de reclutadores

Resultado: Tu título comunica valor inmediatamente, haciendo que los reclutadores quieran leer más sobre vos y considerarte para oportunidades.`,
      keyConcepts: [
        {
          name: 'Títulos Específicos vs Genéricos',
          description: 'Un título específico y orientado a un rol particular convierte mejor que uno genérico o con múltiples roles.',
          characteristics: [
            'Título específico = más conversión',
            'Orientado a un rol particular, no varios',
            'Evita confusión en reclutadores',
            'Más claro = más efectivo',
            'Ejemplo: "Senior Data Engineer, Azure, Databricks, SQL, Python"'
          ],
          examples: [
            '✅ "Senior Data Engineer, Azure, Databricks, SQL, Python"',
            '❌ "Data Manager, Data Engineer, Data Architect" (confuso)',
            '❌ "Data Engineer, Maestría en Data Science" (falta keywords)'
          ]
        },
        {
          name: 'Títulos que Generan Confusión',
          description: 'Los títulos con múltiples roles o sin keywords específicas generan confusión y hacen que los reclutadores te rechacen.',
          characteristics: [
            'Múltiples roles = confusión',
            'Sin keywords específicas = menos visibilidad',
            'Títulos genéricos = no comunican valor',
            'Confusión = rechazo de reclutadores',
            'Ejemplo problemático: "Data Manager, Data Engineer, Data Architect"'
          ],
          examples: [
            '❌ "Data Manager, Data Engineer, Data Architect" → ¿Qué buscás?',
            '❌ "Data Engineer, Maestría en Data Science" → ¿Sabe Python, SQL, Databricks?',
            '❌ Títulos sin keywords = no aparecen en búsquedas'
          ]
        },
        {
          name: 'Títulos Efectivos con Keywords',
          description: 'Los títulos que funcionan tienen el rol específico + keywords top demandadas que los reclutadores buscan.',
          characteristics: [
            'Rol específico claro (ej: Senior Data Engineer)',
            'Keywords top demandadas (Azure, Databricks, SQL, Python)',
            'Específico y orientado a un rol',
            'Comunica valor inmediatamente',
            'Aparece en búsquedas relevantes'
          ],
          examples: [
            '✅ "Senior Data Engineer, Azure, Databricks, SQL, Python"',
            '✅ "Data Engineer, Databricks, SQL, Python"',
            '✅ Títulos que incluyen tecnologías específicas'
          ]
        }
      ],
      bestPractices: [
        'Usar un solo rol específico en el título',
        'Incluir keywords top demandadas',
        'Evitar múltiples roles que generen confusión',
        'Hacer el título específico y claro',
        'Orientar el título al rol que realmente buscás'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Comparación de Títulos en Búsqueda Real',
        description: 'Ejemplo real de cómo un reclutador evalúa títulos en una búsqueda',
        scenario: `Búsqueda del Reclutador:
"Data Engineer"

Títulos que Aparecen:
1. ❌ "Data Engineer, Maestría en Data Science" → No dice si sabe Python, SQL, Azure, AWS, Databricks
2. ❌ "Data Manager, Data Engineer, Data Architect" → Confuso, no específico
3. ✅ "Data Engineer, Databricks, SQL, Python" → Perfecto, lo tomo
4. ✅ "Senior Data Engineer, Azure, Databricks, SQL, Python" → Además es Senior, listo, es lo que busco

Resultado: El reclutador solo considera los perfiles 3 y 4. Los demás quedan fuera.`,
        solution: `Un título efectivo debe ser específico, incluir keywords top demandadas, y estar orientado a un solo rol. Esto hace que aparezcas en búsquedas relevantes y que los reclutadores te consideren inmediatamente.`
      }
    ]
  },
  {
    id: 8,
    level: 'INTERMEDIO',
    title: 'Palabras Clave Estratégicas',
    description: 'Aprendé qué palabras clave usar en tu perfil según las tecnologías más demandadas del mercado.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1061, // "demandadas. Entonces, la probabilidad que esas palabras claves llamen los"
    content: {
      overview: `Las palabras clave estratégicas son el puente entre tu perfil y las búsquedas de reclutadores. Cada industria y rol tiene un conjunto específico de keywords que los reclutadores utilizan para encontrar candidatos. Incluir las palabras clave correctas determina si aparecés o no en los resultados de búsqueda.

Las palabras clave más efectivas son aquellas con alta demanda en el mercado actual. En el contexto de ingeniería de datos, por ejemplo, las tecnologías más demandadas incluyen Python (la más solicitada), SQL (segunda en demanda), y Databricks (creciendo constantemente, presente en 17-20% de las publicaciones). Las plataformas cloud como Azure, AWS y GCP también son altamente demandadas.

La estrategia no es simplemente incluir cualquier keyword, sino identificar y priorizar aquellas que tienen mayor demanda y menor competencia. Esto maximiza tu visibilidad mientras mantiene la relevancia de tu perfil.`,
      why: `¿Por qué las palabras clave estratégicas son esenciales?

Porque determinan tu visibilidad en LinkedIn. Sin las keywords correctas, quedás invisible para los reclutadores, sin importar tu experiencia.

El problema:
- Filtrado automático: LinkedIn solo muestra perfiles con las keywords buscadas
- Keywords obsoletas: Tecnologías antiguas (Java, Hadoop) tienen menos demanda
- Falta de investigación: No saber qué keywords buscan los reclutadores
- Oportunidades perdidas: Perdés chances por no tener keywords relevantes

- Impacto: Incluir keywords top demandadas puede aumentar exponencialmente tu visibilidad y oportunidades, mientras que usar keywords obsoletas o no incluir ninguna te hace invisible.`,
      how: `¿Cómo identificar y usar palabras clave estratégicas?

1. Investigar keywords top demandadas:
- Analizar publicaciones de trabajo en tu área
- Identificar tecnologías, herramientas y habilidades más mencionadas
- Revisar perfiles de profesionales exitosos en tu rol
- Consultar con reclutadores qué keywords buscan

2. Priorizar según demanda:
- Alta demanda: Python, SQL, Databricks, Azure, AWS, GCP
- Crecimiento: Databricks (17-20% de publicaciones, creciendo)
- Evitar: Tecnologías obsoletas (Java, Hadoop) con baja demanda
- Certificaciones: Incluir si son relevantes (ej: "Azure Certified")

3. Incluir estratégicamente:
- Título: 3-5 keywords principales
- "Sobre mí": Mencionar keywords en contexto natural
- Experiencia: Usar keywords en descripciones de roles
- Habilidades: Agregar todas las keywords relevantes

4. Actualizar según tendencias:
- Monitorear alertas de LinkedIn sobre demanda
- Agregar nuevas keywords cuando aumentan en el mercado
- Remover keywords obsoletas o poco demandadas
- Mantener perfil alineado con tendencias actuales

Resultado: Tu perfil aparece en búsquedas relevantes, aumentando significativamente tu visibilidad y oportunidades de ser contactado por reclutadores.`,
      keyConcepts: [
        {
          name: 'Palabras Clave por Rubro',
          description: 'Las palabras clave dependen del rubro en el que estés. Cada área tiene sus keywords específicas que buscan los reclutadores.',
          characteristics: [
            'Keywords específicas por rubro/rol',
            'Dependen de lo que buscan los reclutadores',
            'Deben estar en el perfil para aparecer en búsquedas',
            'Case insensitive (mayúsculas/minúsculas no importan)',
            'Ejemplo para Data Engineering: GCP, AWS, Azure, Databricks, SQL, Python'
          ],
          examples: [
            'Nubes: GCP, AWS, Azure',
            'Herramientas: Databricks, Data Factory',
            'Lenguajes: Python, SQL',
            'Certificaciones: Azure Certified'
          ]
        },
        {
          name: 'Tecnologías Top Demandadas',
          description: 'Algunas tecnologías tienen más demanda que otras. Incluir las top demandadas aumenta significativamente tu visibilidad.',
          characteristics: [
            'Python: Mayor demanda en datos',
            'SQL: Segunda más demandada',
            'Databricks: 17-20% de publicaciones (creciendo)',
            'Azure, AWS, GCP: Nubes más demandadas',
            'Incluir top demandadas = más oportunidades'
          ],
          examples: [
            'Top demandadas: Python, SQL, Databricks, Azure, AWS',
            'Estadística: Python es la más pedida',
            'Databricks crece cada día más (17-20% de publicaciones)'
          ]
        },
        {
          name: 'Tecnologías Obsoletas vs Demandadas',
          description: 'Algunas tecnologías ya no son tan demandadas. Usar keywords obsoletas reduce tus oportunidades.',
          characteristics: [
            'Tecnologías obsoletas: Java, Hadoop, Hype',
            'Menos demanda = menos oportunidades',
            'Probabilidad menor de que llamen a reclutadores',
            'En cambio: Azure, Databricks, SQL, Python = alta demanda',
            'Actualizar keywords según tendencias del mercado'
          ],
          examples: [
            '❌ Java, Hadoop, Hype = tecnologías antiguas, menos demanda',
            '✅ Azure, Databricks, SQL, Python = alta demanda',
            'Incluir tecnologías actuales = más oportunidades'
          ]
        },
        {
          name: 'Certificaciones como Keywords',
          description: 'A veces el tema de certificaciones puede pesar un poco más porque dice que puede certificar los conocimientos que tiene.',
          characteristics: [
            'Certificaciones demuestran conocimientos',
            'No significa que sepas más, significa que puedes certificarlos',
            'Puede demostrar que tiene los conocimientos',
            'Ejemplo: "Azure Certified"',
            'Agregan credibilidad al perfil'
          ],
          examples: [
            '"Azure Certified" como keyword',
            'Certificaciones validan conocimientos',
            'Pueden hacer la diferencia en búsquedas'
          ]
        }
      ],
      bestPractices: [
        'Incluir keywords top demandadas en tu perfil',
        'Actualizar keywords según tendencias del mercado',
        'Evitar tecnologías obsoletas o poco demandadas',
        'Usar keywords específicas de tu rubro',
        'Incluir certificaciones relevantes como keywords',
        'Revisar qué keywords buscan los reclutadores en tu área'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Comparación de Keywords',
        description: 'Ejemplo de cómo diferentes keywords afectan la visibilidad',
        scenario: `Perfil con Keywords Obsoletas:
- "Data Engineer, Java, Hadoop, Hive"
- Tecnologías antiguas, menos demanda
- Probabilidad menor de que reclutadores te contacten

Perfil con Keywords Top Demandadas:
- "Data Engineer, Databricks, AWS, Python, SQL"
- Tecnologías actuales, alta demanda
- Aparece en más búsquedas relevantes
- Más oportunidades de ser contactado`,
        solution: `Incluir keywords top demandadas es crítico. Las tecnologías obsoletas reducen tus oportunidades, mientras que las top demandadas (Python, SQL, Databricks, Azure, AWS) aumentan significativamente tu visibilidad y chances de ser contactado.`
      }
    ]
  },
  {
    id: 9,
    level: 'INTERMEDIO',
    title: 'Foto Profesional',
    description: 'Aprendé cómo una foto profesional puede generar afinidad y aumentar tus chances de ser contactado.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1087, // "versión chica de la foto. una una foto no tiene poder ser profesional, o sea,"
    content: {
      overview: `Tu foto de perfil es parte fundamental del hook que capta la atención en los primeros segundos. Aunque pueda parecer superficial, una foto profesional bien ejecutada genera afinidad y confianza, elementos críticos para que un reclutador quiera conocer más sobre vos.

Una foto profesional efectiva combina seriedad con accesibilidad. Debe transmitir competencia y profesionalismo, pero también calidez y cercanía. La clave está en el balance: demasiado formal puede parecer distante, mientras que demasiado casual puede transmitir falta de profesionalismo.

Los elementos técnicos también importan: buena iluminación que muestre claramente tu rostro, vestimenta apropiada para tu industria, y un fondo que no distraiga. Estos detalles, aunque pequeños, contribuyen significativamente a la primera impresión que generás.`,
      why: `¿Por qué una foto profesional importa?

Porque es parte del hook que determina si el reclutador sigue leyendo o pasa al siguiente perfil. Genera afinidad y confianza en segundos.

Impacto psicológico:
- Afinidad: Una sonrisa genuina genera conexión emocional
- Confianza: Una foto profesional transmite competencia
- Primera impresión: Determina el tono de toda la interacción
- Diferenciación: Te distingue de perfiles sin foto o con fotos poco profesionales

- El problema sin foto profesional: Una foto borrosa, con mala iluminación, o demasiado casual puede hacer que un reclutador pase de largo, incluso si tu experiencia es perfecta.`,
      how: `¿Cómo crear una foto profesional efectiva?

1. Elementos técnicos:
- Iluminación: Luces enfrente, cara clara y visible
- Calidad: Foto nítida, no borrosa o pixelada
- Enfoque: Tu cara debe ser el centro de atención
- Fondo: Neutro o profesional, sin distractores

2. Elementos de presentación:
- Vestimenta: Apropiada para tu industria
- Expresión: Sonrisa genuina (genera afinidad)
- Postura: Confiada y profesional
- Sin elementos distractores: Sin gorras, gafas oscuras, o filtros

3. Evitar errores comunes:
- ❌ Foto borrosa o pixelada
- ❌ Mala iluminación (cara en sombra)
- ❌ Elementos que tapen la cara (gorras, gafas oscuras)
- ❌ Filtros o efectos excesivos
- ❌ Fotos demasiado casuales o informales

4. Testear efectividad:
- Pedir feedback a colegas sobre primera impresión
- Comparar con fotos de profesionales exitosos en tu área
- Ajustar según feedback recibido

Resultado: Tu foto genera afinidad y confianza inmediatamente, complementando tu título para crear un hook efectivo que capta la atención de reclutadores.`,
      keyConcepts: [
        {
          name: 'Características de Foto Profesional',
          description: 'Una foto profesional debe ser seria pero accesible, con buena iluminación y que muestre claramente tu cara.',
          characteristics: [
            'Foto seria pero accesible',
            'Vestimenta apropiada',
            'Sin gorras que tapen la cara',
            'Cara clara y visible',
            'Buena iluminación (luces enfrente)'
          ],
          examples: [
            'Foto con traje o vestimenta profesional',
            'Cara completamente visible',
            'Buena iluminación que muestre claramente los rasgos'
          ]
        },
        {
          name: 'El Poder de la Sonrisa',
          description: 'Una sonrisa en la foto genera más afinidad y hace que el perfil sea más atractivo para los reclutadores.',
          characteristics: [
            'Sonrisa genera más afinidad',
            'Hace el perfil más accesible',
            'Genera conexión emocional',
            'Suma puntos, por más que parezca que no',
            'Diferencia entre foto seria vs sonriente'
          ],
          examples: [
            'Foto con sonrisa = más afinidad',
            'Foto seria = menos conexión emocional',
            'La sonrisa puede hacer la diferencia'
          ]
        },
        {
          name: 'Iluminación y Claridad',
          description: 'Una foto con buena iluminación y donde se ve claramente la cara es más efectiva que una oscura o borrosa.',
          characteristics: [
            'Luces enfrente para buena iluminación',
            'Cara se ve de forma clara',
            'Foto nítida, no borrosa',
            'Buena calidad de imagen',
            'Impacto visual positivo'
          ],
          examples: [
            'Foto con luces enfrente = cara clara',
            'Foto oscura = menos impacto',
            'Buena iluminación = foto profesional'
          ]
        }
      ],
      bestPractices: [
        'Usar foto profesional con buena iluminación',
        'Incluir sonrisa para generar afinidad',
        'Asegurarse de que la cara se vea claramente',
        'Evitar gorras o elementos que tapen la cara',
        'Usar vestimenta apropiada para el contexto profesional',
        'Invertir en una buena foto profesional si es necesario'
      ]
    }
  },
  {
    id: 10,
    level: 'INTERMEDIO',
    title: 'El Poder de la Claridad: Menos es Más',
    description: 'Aprendé por qué la especialización y la claridad son más efectivas que la generalización en tu perfil.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 951, // "tengo un data engineer que además sabe datab, sabe SQL, sabe Python. Perfecto,"
    content: {
      overview: `El principio "menos es más" aplica directamente a la optimización de perfiles de LinkedIn. La claridad y especialización generan más oportunidades que la generalización y ambigüedad. Un perfil que comunica un mensaje claro y específico es infinitamente más efectivo que uno que intenta mostrar múltiples roles o habilidades de forma confusa.

El problema surge cuando los profesionales intentan mostrar todo lo que saben o todos los roles que podrían desempeñar. Esto genera ambigüedad: si tu perfil dice "Data Engineer, Data Architect, Data Manager", el reclutador no sabe qué buscás realmente. ¿Sos ingeniero? ¿Arquitecto? ¿Manager? Esta confusión hace que los reclutadores pasen de largo, prefiriendo perfiles claros y específicos.

La especialización no significa limitar tus oportunidades, sino comunicar claramente tu valor principal. Un perfil especializado en un rol específico con keywords estratégicas atrae más oportunidades que un perfil genérico que intenta abarcar todo.`,
      why: `¿Por qué "menos es más" funciona en LinkedIn?

Porque la claridad genera confianza y acción. Los reclutadores prefieren perfiles específicos que saben exactamente qué buscan.

El problema de la generalización:
- Ambigüedad: Múltiples roles confunden a reclutadores
- Falta de foco: No comunica valor específico
- Rechazo por confusión: Reclutadores pasan de largo
- Algoritmo confundido: LinkedIn no sabe cómo asociarte

Impacto de la especialización:
- Claridad: Reclutadores saben exactamente qué buscás
- Confianza: Transmite seguridad y enfoque
- Más oportunidades: Perfiles claros atraen más ofertas
- Asociación algorítmica: LinkedIn te asocia correctamente

- Cita de Warren Buffett: "Especialícense en algo. No traten de ser generalistas en todas las cosas." Esto aplica directamente a tu perfil de LinkedIn.`,
      how: `¿Cómo aplicar "menos es más" en tu perfil?

1. Elegir un solo rol principal:
- Si querés ser Data Engineer, poné solo "Data Engineer"
- Si querés ser Manager, poné solo "Data Manager"
- No intentes mostrar múltiples roles simultáneamente
- Dejá que el proceso de selección determine el nivel (junior/senior)

2. Especializarte en keywords específicas:
- Incluir habilidades principales que más usás
- Priorizar tecnologías con alta demanda
- Evitar incluir todo lo que alguna vez usaste
- Ejemplo: 4 años Python > 2 meses Java

3. Evitar títulos genéricos:
- ❌ "Especialista en datos" (¿qué es eso?)
- ❌ "Programación" (es un medio, no un rol)
- ❌ Múltiples roles separados por comas
- ✅ "Senior Data Engineer, Databricks, AWS, Python, SQL"

4. Comunicar valor específico:
- Enfocarse en un área de expertise
- Mostrar profundidad, no amplitud
- Dejar que las cosas "caigan por su propio peso"
- No forzar múltiples mensajes

Resultado: Tu perfil comunica un mensaje claro y específico, atrayendo oportunidades alineadas con tu especialización y generando más confianza en reclutadores.`,
      keyConcepts: [
        {
          name: 'Menos es Más: Filosofía de Claridad',
          description: 'La claridad viene de la especialización, no de mostrar todo lo que sabés. Menos información pero más clara es más efectiva.',
          characteristics: [
            'Menos es más: frase clave',
            'Especialización vs generalización',
            'Un rol claro es mejor que múltiples roles',
            'Claridad genera confianza',
            'Menos confusión = más oportunidades'
          ],
          examples: [
            'Un rol específico = claro y efectivo',
            'Múltiples roles = confusión y menos oportunidades',
            'Claridad atrae, ambigüedad rechaza'
          ]
        },
        {
          name: 'Especialización vs Generalización',
          description: 'Warren Buffett dice: "Especialícense en algo. No traten de ser generalistas en todas las cosas." Lo mismo aplica al perfil de LinkedIn.',
          characteristics: [
            'Especialización = más efectiva',
            'Generalización = menos efectiva',
            'Un solo rol bien definido',
            'Especialícense en algo específico',
            'Lo mismo en el perfil: especialícense en algo'
          ],
          examples: [
            'Cita de Warren Buffett sobre especialización',
            'Especializarse en un rol = más oportunidades',
            'Ser generalista = menos oportunidades específicas'
          ]
        },
        {
          name: 'El Problema de Múltiples Roles',
          description: 'Cuando tenés múltiples roles en tu perfil, generás inconsistencia y confusión. Los reclutadores no saben qué buscás.',
          characteristics: [
            'Múltiples roles = inconsistencia',
            'Reclutadores no saben qué buscás',
            'Genera preguntas en lugar de respuestas',
            'Confusión = rechazo',
            'Ejemplo: Data Engineer + Data Architect + Data Manager'
          ],
          examples: [
            '❌ "Data Engineer, Data Architect, Data Manager" → ¿Qué querés ser?',
            '✅ "Senior Data Engineer" → Claro y específico',
            'Múltiples roles = menos oportunidades'
          ]
        },
        {
          name: 'Si Querés Ser Manager, Manager',
          description: 'Si querés ser manager, poné manager. Si querés ser arquitecto de datos, poné arquitecto de datos. Un solo rol claro.',
          characteristics: [
            'Un solo rol específico',
            'Si querés ser manager, manager',
            'Si querés ser arquitecto, arquitecto',
            'No mezclar múltiples roles',
            'Claridad sobre ambigüedad'
          ],
          examples: [
            'Si buscás ser manager → "Data Manager"',
            'Si buscás ser arquitecto → "Data Architect"',
            'Un solo rol = más claro y efectivo'
          ]
        }
      ],
      bestPractices: [
        'Elegir un solo rol específico para tu perfil',
        'Evitar múltiples roles que generen confusión',
        'Especializarse en algo, no ser generalista',
        'Aplicar "menos es más" a tu perfil',
        'Priorizar claridad sobre mostrar todo lo que sabés'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Antes y Después: Claridad',
        description: 'Comparación de perfil con múltiples roles vs un solo rol claro',
        scenario: `Perfil con Múltiples Roles (Confuso):
- "Data Engineer, Data Architect, Data Manager"
- Reclutador: "¿Qué quiere ser? ¿Ingeniero? ¿Arquitecto? ¿Manager?"
- Confusión = rechazo
- Menos oportunidades

Perfil con Un Solo Rol (Claro):
- "Senior Data Engineer, Databricks, AWS, Python, SQL"
- Reclutador: "Perfecto, es lo que busco"
- Claridad = oportunidad
- Más oportunidades`,
        solution: `La claridad viene de la especialización. Un perfil con un solo rol específico y keywords claras es mucho más efectivo que uno con múltiples roles. Menos es más: un rol claro genera más oportunidades que múltiples roles confusos.`
      }
    ]
  },
  {
    id: 11,
    level: 'INTERMEDIO',
    title: 'Títulos Ambiguos a Evitar',
    description: 'Aprendé qué títulos genéricos evitar porque generan confusión y hacen que los reclutadores te rechacen.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1450, // "terreno, a la ingeniería de datos. Entonces, eviten esos títulos ambiguos o"
    content: {
      overview: `Los títulos genéricos y ambiguos son uno de los errores más comunes en perfiles de LinkedIn. Términos como "Especialista en datos" o simplemente "Programación" generan más preguntas que respuestas, haciendo que los reclutadores pasen de largo en lugar de considerar tu perfil.

El problema fundamental es que estos títulos no comunican valor específico. Cuando un reclutador ve "Especialista en datos", se pregunta: ¿Es arquitecto? ¿Ingeniero? ¿Científico? ¿Analista? Esta ambigüedad genera confusión y, en un mercado competitivo, los reclutadores prefieren perfiles claros que no requieran interpretación.

Es importante entender que ciertos términos, como "Programación", no son roles sino medios. Programás porque sos ingeniero de datos, científico de datos, o desarrollador backend, pero "Programación" en sí mismo no define quién sos profesionalmente.`,
      why: `¿Por qué evitar títulos ambiguos?

Porque generan confusión y hacen que los reclutadores te rechacen. En un mercado competitivo, la claridad es crítica.

El problema:
- Generan preguntas: "¿Qué es un especialista en datos?" en lugar de respuestas
- Confusión: Reclutadores no saben qué buscás o qué hacés
- Rechazo inmediato: Prefieren perfiles claros que no requieran interpretación
- Pérdida de oportunidades: Perdés chances por falta de especificidad

- Impacto: Un título ambiguo puede hacer que un reclutador pase de largo, incluso si tu experiencia es perfecta. La claridad atrae, la ambigüedad rechaza.`,
      how: `¿Cómo evitar títulos ambiguos?

1. Identificar títulos problemáticos:
- ❌ "Especialista en datos" (¿qué es eso?)
- ❌ "Especialista en IA" (demasiado genérico)
- ❌ "Programación" (es un medio, no un rol)
- ❌ Términos vagos que no comunican valor específico

2. Reemplazar con títulos específicos:
- ✅ "Senior Data Engineer, Databricks, AWS, Python, SQL"
- ✅ "Data Architect, Azure, Data Factory"
- ✅ "Data Scientist, Machine Learning, Python"
- ✅ Títulos que comunican rol + tecnologías específicas

3. Entender la diferencia:
- Rol: Data Engineer, Data Architect, Data Manager
- Medio: Programación, Análisis, Diseño
- Especialización: Backend, Frontend, Full Stack
- El título debe comunicar el rol, no el medio

4. Testear claridad:
- ¿Un reclutador entendería inmediatamente qué buscás?
- ¿Genera respuestas o preguntas?
- ¿Es específico o genérico?

Resultado: Tu título comunica valor específico inmediatamente, eliminando ambigüedad y aumentando tus chances de ser considerado por reclutadores.`,
      keyConcepts: [
        {
          name: 'Títulos Genéricos que Generan Preguntas',
          description: 'Títulos como "Especialista en datos" o "Programación" generan más preguntas que respuestas, haciendo que los reclutadores te rechacen.',
          characteristics: [
            '"Especialista en datos" → ¿Qué es? ¿Qué hace?',
            'Genera preguntas en lugar de respuestas',
            'Reclutadores no saben qué buscás',
            'Confusión = rechazo',
            'Ejemplos: "Especialista en datos", "Especialista en IA", "Programación"'
          ],
          examples: [
            '❌ "Especialista en datos" → ¿Arquitecto? ¿Ingeniero? ¿Científico?',
            '❌ "Especialista en IA" → Demasiado genérico',
            '❌ "Programación" → ¿En qué? ¿Cómo?'
          ]
        },
        {
          name: 'Programación es un Medio, No un Rol',
          description: 'La programación es una forma de hacer algo, no es el rol que te define. Programás porque sos ingeniero de datos, científico de datos, etc.',
          characteristics: [
            'Programación es un medio, no un rol',
            'Programás porque sos ingeniero de datos',
            'Programás porque sos científico de datos',
            'Programás backend, frontend, full stack',
            'No es el rol, es la herramienta'
          ],
          examples: [
            '❌ "Programación" → ¿En qué? ¿Cómo?',
            '✅ "Data Engineer" → Rol específico que programa',
            'La programación es la herramienta, no el rol'
          ]
        },
        {
          name: 'Especificar en el Título',
          description: 'En vez de títulos genéricos, especificá exactamente qué sos y qué hacés. Sé claro y específico.',
          characteristics: [
            'Especificar el rol exacto',
            'Evitar términos genéricos',
            'Ser claro sobre qué buscás',
            'Títulos específicos = más oportunidades',
            'Ejemplo: "Senior Data Engineer" en vez de "Especialista en datos"'
          ],
          examples: [
            '✅ "Senior Data Engineer, Databricks, AWS, Python, SQL"',
            '❌ "Especialista en datos"',
            'Especificar = claridad = oportunidades'
          ]
        }
      ],
      bestPractices: [
        'Evitar títulos genéricos como "Especialista en datos"',
        'No usar "Programación" como rol (es un medio, no un rol)',
        'Especificar exactamente qué sos y qué buscás',
        'Usar títulos claros y específicos',
        'Generar respuestas, no preguntas con tu título'
      ]
    }
  },
  {
    id: 12,
    level: 'INTERMEDIO',
    title: 'Aprovechar la Ambigüedad (Estrategia)',
    description: 'Aprendé cómo aprovechar la ambigüedad estratégicamente sin mentir, comunicando tu valor como el rol que buscás.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 3, // "La idea de esta charla es ser simple, es que ustedes puedan aprovechar lo que ya"
    content: {
      overview: `Existe una diferencia importante entre mentir y aprovechar estratégicamente la ambigüedad. Si te estás capacitando para un rol o tenés experiencia relacionada que se alinea con ese rol, podés comunicar tu valor como ese rol objetivo sin comprometer tu autenticidad.

El principio clave es la honestidad: nunca mentir sobre experiencia que no tenés. Sin embargo, si desarrollaste proyectos que un arquitecto de datos haría, si comunicaste con otras áreas para estandarizar procesos, o si modelaste datos y dimensiones, tenés experiencia relevante que podés comunicar como ese rol.

La estrategia es comunicar tu valor como el rol que buscás, no como el rol actual si estás en transición. Esto aumenta las probabilidades de que lleguen oportunidades alineadas con tu objetivo profesional, incluso si la mayoría de propuestas siguen siendo del rol actual.`,
      why: `¿Por qué aprovechar la ambigüedad estratégicamente?

Porque te permite comunicar tu valor como el rol objetivo, aumentando las probabilidades de recibir oportunidades alineadas con tus aspiraciones profesionales.

La diferencia clave:
- Mentir: Decir que trabajaste en algo que no hiciste
- Aprovechar ambigüedad: Comunicar experiencia relacionada como el rol objetivo
- Autenticidad: Ser honesto sobre tus capacidades y preparación
- Estrategia: Maximizar oportunidades sin comprometer integridad

- El problema sin estrategia: Si comunicás tu valor solo como tu rol actual, las oportunidades que lleguen serán de ese rol, no del que buscás.`,
      how: `¿Cómo aprovechar la ambigüedad estratégicamente?

1. Evaluar tu experiencia relacionada:
- ¿Desarrollaste proyectos que haría el rol objetivo?
- ¿Tenés habilidades transferibles relevantes?
- ¿Te estás capacitando activamente en ese rol?
- ¿Tenés experiencia que se alinea con el rol objetivo?

2. Comunicar como el rol objetivo:
- Si buscás ser Data Architect → comunicar como Data Architect
- Si desarrollaste proyectos de arquitectura → destacarlos
- Si te estás capacitando → mencionarlo estratégicamente
- No comunicar como rol actual si buscás cambiar

3. Mantener autenticidad:
- ❌ No mentir sobre experiencia que no tenés
- ❌ No exagerar capacidades
- ✅ Ser honesto sobre preparación y experiencia relacionada
- ✅ Comunicar valor real, no inventado

4. Balancear expectativas:
- La mayoría de propuestas seguirán siendo del rol actual
- Pero aumenta la probabilidad de recibir oportunidades del rol objetivo
- Cada oportunidad del rol objetivo es valiosa

Resultado: Comunicás tu valor estratégicamente como el rol que buscás, aumentando las probabilidades de recibir oportunidades alineadas con tus objetivos profesionales, sin comprometer tu autenticidad.`,
      keyConcepts: [
        {
          name: 'No Mentir, Mantener Autenticidad',
          description: 'Nunca mientas en tu perfil. Mantené tu autenticidad. Pero donde hay ambigüedad, podés aprovecharla estratégicamente.',
          characteristics: [
            'No mientan, sean honestos',
            'Mantengan su autenticidad',
            'No poner "astronauta" si picás paredes',
            'Ser honesto sobre tus capacidades',
            'Aprovechar ambigüedad sin mentir'
          ],
          examples: [
            '❌ No mentir sobre experiencia que no tenés',
            '✅ Ser honesto pero aprovechar ambigüedad',
            'Autenticidad es fundamental'
          ]
        },
        {
          name: 'Aprovechar Ambigüedad Estratégicamente',
          description: 'Si te estás preparando para un rol o tenés experiencia relacionada, podés comunicar tu valor como ese rol.',
          characteristics: [
            'Si te estás capacitando en algo, comunicarlo como ese rol',
            'Si desarrollaste proyectos de ese rol, comunicarlo',
            'Aprovechar ambigüedad sin mentir',
            'Comunicar valor como el rol que buscás',
            'Ejemplo: Si te preparás como Data Architect, comunicarlo como Data Architect'
          ],
          examples: [
            'Si desarrollaste proyectos de arquitecto → comunicar como Data Architect',
            'Si te estás capacitando → comunicar como ese rol',
            'Aprovechar experiencia relacionada estratégicamente'
          ]
        },
        {
          name: 'Comunicar Valor del Rol Objetivo',
          description: 'Si buscás ser Data Architect, comunicá tu valor como Data Architect, no como Data Engineer, incluso si aún no trabajaste formalmente en ese rol.',
          characteristics: [
            'Comunicar valor como el rol que buscás',
            'No como el rol actual si buscás cambiar',
            'Si buscás ser arquitecto, comunicar como arquitecto',
            'No todas las propuestas serán del rol actual',
            'Pequeña probabilidad de que llegue la oportunidad del rol objetivo'
          ],
          examples: [
            'Si buscás ser Data Architect → comunicar como Data Architect',
            'No comunicar como Data Engineer si buscás ser Architect',
            'Comunicar valor del rol objetivo aumenta probabilidades'
          ]
        }
      ],
      bestPractices: [
        'Nunca mentir, mantener autenticidad',
        'Aprovechar ambigüedad estratégicamente',
        'Comunicar valor como el rol que buscás',
        'Si te estás capacitando, comunicarlo como ese rol',
        'Ser honesto pero estratégico'
      ]
    }
  },
  {
    id: 13,
    level: 'INTERMEDIO',
    title: 'Habilidades Principales: Priorizar',
    description: 'Aprendé a priorizar las habilidades que más usás y que son más fuertes, no las más recientes.',
    difficulty: 'intermediate',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1531, // "descubrir. Entonces siempre digo, incluyan las habilidades principales eh"
    content: {
      overview: `La estrategia para incluir habilidades en tu perfil debe basarse en fortaleza y relevancia, no en recencia. Priorizar las habilidades que más usás y en las que tenés mayor expertise aumenta tu credibilidad y atractivo para reclutadores.

El error común es incluir habilidades recientes pero poco desarrolladas, mientras se omiten habilidades fuertes con años de experiencia. Por ejemplo, si usaste Java durante 2 meses pero Python durante 4 años, Python debe tener prioridad en tu perfil, independientemente de cuál usaste más recientemente.

Esta priorización comunica tu verdadera expertise y aumenta tu credibilidad. Los reclutadores buscan profundidad y dominio, no una lista larga de tecnologías con experiencia superficial.`,
      why: `¿Por qué priorizar habilidades por fortaleza?

Porque comunica tu verdadera expertise y aumenta tu credibilidad. Los reclutadores buscan profundidad, no una lista larga de tecnologías con experiencia superficial.

El problema de priorizar por recencia:
- Experiencia superficial: Incluir tecnologías usadas poco tiempo
- Omitir fortalezas: Dejar fuera habilidades con años de experiencia
- Credibilidad reducida: Lista larga pero poco profunda
- Menos atractivo: Reclutadores buscan expertise, no superficialidad

Impacto de priorizar por fortaleza:
- Credibilidad: Muestra verdadera expertise
- Atractivo: Reclutadores valoran profundidad
- Claridad: Comunica tus fortalezas reales
- Más oportunidades: Perfiles con expertise claro atraen más ofertas`,
      how: `¿Cómo priorizar habilidades estratégicamente?

1. Evaluar por fortaleza, no recencia:
- ¿Qué habilidades usaste más tiempo?
- ¿En cuáles tenés mayor expertise?
- ¿Cuáles son tus fortalezas reales?
- Ejemplo: 4 años Python > 2 meses Java

2. Incluir habilidades principales:
- Priorizar habilidades con mayor experiencia
- Incluir tecnologías donde tenés dominio real
- Evitar habilidades con experiencia superficial
- Enfocarse en profundidad, no en cantidad

3. Alinear con demanda del mercado:
- Priorizar habilidades top demandadas si las tenés
- Balancear fortaleza personal con demanda del mercado
- Incluir tecnologías relevantes para tu rol objetivo
- Actualizar según tendencias si aplica

4. Dejar que el proceso determine nivel:
- No especificar nivel (junior/senior) prematuramente
- Dejar que las entrevistas determinen tu nivel real
- Comunicar fortalezas, el proceso definirá el nivel
- Enfocarse en expertise, no en títulos de nivel

Resultado: Tu perfil comunica tus fortalezas reales, aumentando credibilidad y atractivo para reclutadores que buscan expertise genuino.`,
      keyConcepts: [
        {
          name: 'Priorizar Habilidades por Fortaleza',
          description: 'Incluí las habilidades que más usás y que son más fuertes, no las que usaste poco tiempo o recientemente.',
          characteristics: [
            'Incluir habilidades que más usás',
            'Priorizar por fortaleza, no por recencia',
            'No poner habilidades que usaste poco tiempo',
            'Ir a lo más fuerte, a lo que ya conocés bien',
            'Ejemplo: 4 años Python > 2 meses Java'
          ],
          examples: [
            'Si usaste 4 años Python y 2 meses Java → priorizar Python',
            'No poner Java solo porque es más reciente',
            'Ir a lo más fuerte y conocido'
          ]
        },
        {
          name: 'No Priorizar por Recencia',
          description: 'No pongas habilidades solo porque las usaste recientemente si tenés otras que usaste mucho más tiempo y conocés mejor.',
          characteristics: [
            'No priorizar por recencia',
            'Priorizar por fortaleza y conocimiento',
            'Habilidades que conocés bien > habilidades recientes',
            'Ir a lo más fuerte, no a lo más nuevo',
            'Ejemplo: No poner Java (2 meses) sobre Python (4 años)'
          ],
          examples: [
            '❌ Priorizar Java (2 meses) sobre Python (4 años)',
            '✅ Priorizar Python (4 años) sobre Java (2 meses)',
            'Fortaleza > Recencia'
          ]
        },
        {
          name: 'Dejar que el Proceso Determine el Nivel',
          description: 'No te preocupes por especificar si sos junior, semi-senior o senior. Dejá que el proceso de selección lo determine.',
          characteristics: [
            'No especificar nivel (junior/senior) en título',
            'Dejá que el proceso de selección lo determine',
            'Las cosas caen por su propio peso',
            'Hacé entrevistas para descubrir tu nivel',
            'El proceso te dirá si sos junior, semi-senior o senior'
          ],
          examples: [
            'No poner "Junior Data Engineer" si no estás seguro',
            'Poner "Data Engineer" y dejar que el proceso determine',
            'Las entrevistas te dirán tu nivel real'
          ]
        }
      ],
      bestPractices: [
        'Priorizar habilidades que más usás y conocés mejor',
        'No priorizar por recencia, priorizar por fortaleza',
        'Incluir habilidades principales, no todas',
        'Dejar que el proceso determine tu nivel',
        'Ir a lo más fuerte, a lo que ya conocés bien'
      ]
    }
  },
  {
    id: 14,
    level: 'AVANZADO',
    title: 'Sobre Mí (About/Summary)',
    description: 'Aprendé cómo estructurar tu sección "Sobre Mí" para reforzar tu rol y comunicar valor efectivamente.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 3663, // "que tengan, o sea, simplificar su título, reescribir sobre ustedes,"
    content: {
      overview: `La sección "Sobre Mí" es tu oportunidad de reforzar tu rol y comunicar valor de forma más completa que en el título. El primer párrafo debe reforzar inmediatamente tu rol principal, usando formatos como "Data Engineer especializado en..." o "Data Engineer con conocimientos en...".

La estructura efectiva del "Sobre Mí" sigue un patrón claro: el primer párrafo refuerza tu rol y especialización, los párrafos siguientes desarrollan tu enfoque actual y experiencia relevante. Esta estructura permite que el reclutador entienda rápidamente quién sos y qué valor ofrecés.

La consistencia entre tu título y tu "Sobre Mí" es crítica. Si tu título menciona "Mentorías orientadas a datos", tu "Sobre Mí" debe hablar de mentorías. Esta coherencia refuerza tu mensaje y aumenta la credibilidad de tu perfil.`,
      why: `¿Por qué la sección "Sobre Mí" es importante?

Porque es donde desarrollás tu valor después de captar atención con el hook. Es tu oportunidad de reforzar tu rol y comunicar valor completo.

El problema sin estructura:
- Falta de refuerzo: No reforzás tu rol desde el inicio
- Inconsistencia: Título y "Sobre Mí" no están alineados
- Mensaje confuso: No comunica valor claramente
- Oportunidad perdida: No aprovechás el espacio para comunicar valor

- Impacto: Un "Sobre Mí" bien estructurado refuerza tu mensaje y aumenta significativamente tus chances de ser considerado, mientras que uno mal estructurado puede hacer que un reclutador pierda interés.`,
      how: `¿Cómo estructurar el "Sobre Mí" efectivamente?

1. Primer párrafo: Reforzar rol:
- Empezar con tu rol específico: "Data Engineer especializado en..."
- Incluir tecnologías o áreas de especialización
- Comunicar que ya sos ese rol, no que querés serlo
- Formato: "Data Engineer especializado en Databricks y AWS"

2. Párrafos siguientes: Desarrollo:
- Segundo párrafo: Enfoque actual o proyectos relevantes
- Tercer párrafo: Experiencia y logros destacados
- Mantener coherencia con el mensaje del título
- Incluir keywords estratégicas naturalmente

3. Consistencia con título:
- Si título menciona "Mentorías", "Sobre Mí" habla de mentorías
- Si título menciona tecnologías, "Sobre Mí" las desarrolla
- Reforzar el mismo mensaje en ambos lugares
- Crear coherencia narrativa

4. Optimización continua:
- Revisar y ajustar según feedback
- Actualizar keywords según tendencias
- Mantener mensaje claro y profesional
- Testear efectividad con reclutadores

Resultado: Tu "Sobre Mí" refuerza tu rol y comunica valor completo, creando coherencia con tu título y aumentando significativamente tus chances de ser considerado por reclutadores.`,
      keyConcepts: [
        {
          name: 'Reforzar Rol desde el Primer Renglón',
          description: 'El primer párrafo del "Sobre Mí" debe reforzar tu rol principal. Data Engineer especializado en... o Data Engineer con conocimientos en...',
          characteristics: [
            'Primer renglón: reforzar rol principal',
            'Formato: "Data Engineer especializado en..."',
            'O: "Data Engineer con conocimientos en..."',
            'Reforzar la idea en el reclutador',
            'Comunicar que ya sos ese rol'
          ],
          examples: [
            '"Data Engineer especializado en Databricks y AWS"',
            '"Data Engineer con conocimientos en arquitectura de datos"',
            'Reforzar desde el primer párrafo'
          ]
        },
        {
          name: 'Consistencia entre Título y "Sobre Mí"',
          description: 'Lo que decís en el título debe estar reforzado en el "Sobre Mí". Si tu título dice "Mentorías orientadas a datos", tu "Sobre Mí" debe hablar de mentorías.',
          characteristics: [
            'Título y "Sobre Mí" deben ser consistentes',
            'Si título dice "Mentorías", "Sobre Mí" habla de mentorías',
            'Reforzar el mismo mensaje en ambos lugares',
            'Consistencia = claridad',
            'El reclutador ve el mismo mensaje en ambos lugares'
          ],
          examples: [
            'Título: "Mentorías orientadas a datos"',
            '"Sobre Mí": Habla de mentorías de personas',
            'Consistencia entre ambos refuerza el mensaje'
          ]
        },
        {
          name: 'Estructura del "Sobre Mí"',
          description: 'Estructura recomendada: primer párrafo refuerza rol, segundo párrafo habla de enfoque actual, tercer párrafo habla de experiencia.',
          characteristics: [
            'Párrafo 1: Reforzar rol principal',
            'Párrafo 2: Enfoque actual (ej: "Como Senior Data Engineer me encuentro enfocado en...")',
            'Párrafo 3: Experiencia previa (ej: "Con 5 años de experiencia en...")',
            'Cada párrafo refuerza el mismo mensaje',
            'Estructura clara y consistente'
          ],
          examples: [
            'Párrafo 1: "Data Engineer especializado en..."',
            'Párrafo 2: "Como Senior Data Engineer me encuentro enfocado en construir plataformas..."',
            'Párrafo 3: "Con 5 años de experiencia en Ingeniería de Datos..."'
          ]
        }
      ],
      bestPractices: [
        'Reforzar rol principal desde el primer renglón',
        'Mantener consistencia entre título y "Sobre Mí"',
        'Estructurar en párrafos claros',
        'Cada párrafo debe reforzar el mismo mensaje',
        'Optimizar con keywords estratégicas'
      ]
    }
  },
  {
    id: 15,
    level: 'AVANZADO',
    title: 'Historia al Servicio del Rol',
    description: 'Aprendé cómo transformar tu historia personal y experiencias previas en valor para el rol que buscás.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1589, // "entonces ahora? Bueno, a partir de ahí lo que está es la historia al servicio"
    content: {
      overview: `Cada profesional tiene una historia que puede transformarse en valor para el rol que buscás. La clave está en identificar habilidades transferibles de experiencias previas, incluso si no parecen directamente relacionadas con tecnología o el rol objetivo.

Desde los 18 años en adelante, todos tienen experiencias que desarrollaron habilidades valiosas. Un técnico mecánico que diseñó prototipos, analizó conectores, hizo validaciones y optimizó procesos, tiene habilidades directamente transferibles: diseño, análisis, validación, testing y optimización.

El error común es descartar experiencias porque "no son tecnológicas" o "no son del rol". La estrategia correcta es analizar qué habilidades desarrollaste en cada experiencia y cómo se relacionan con el rol que buscás. Cada experiencia previa tiene valor transferible si sabés cómo comunicarlo.`,
      why: `¿Por qué transformar tu historia en valor?

Porque maximiza tu experiencia y comunica valor completo. No necesitás descartar experiencias previas, podés transformarlas en fortalezas.

El problema de descartar experiencias:
- Pérdida de valor: Descartar experiencias valiosas porque "no son tecnológicas"
- Perfil incompleto: No comunicar toda tu experiencia relevante
- Menos oportunidades: Perder chances por no mostrar habilidades transferibles
- Subestimación: No valorar experiencias previas que tienen valor real

- Impacto: Transformar tu historia en valor te permite comunicar experiencia completa y aumentar significativamente tus chances de ser considerado, incluso si no tenés experiencia directa en el rol objetivo.`,
      how: `¿Cómo transformar tu historia en valor?

1. Identificar experiencias previas:
- Listar todas tus experiencias desde los 18 años
- No descartar porque "no son tecnológicas"
- Incluir trabajos, proyectos, estudios, voluntariados
- Cada experiencia tiene algo que aportar

2. Analizar habilidades transferibles:
- ¿Qué habilidades desarrollaste en cada experiencia?
- ¿Cómo se relacionan con el rol objetivo?
- Ejemplo: Técnico mecánico → diseño, análisis, validación, optimización
- Buscar conexiones entre experiencias y rol objetivo

3. Comunicar valor transferible:
- Transformar experiencias en habilidades relevantes
- Ejemplo: "Diseñé prototipos" → "Experiencia en diseño de sistemas"
- Ejemplo: "Analicé conectores" → "Experiencia en análisis de datos"
- Ejemplo: "Hice validaciones" → "Experiencia en testing"

4. Estructurar en el perfil:
- Incluir experiencias previas en "Sobre Mí" o experiencia
- Comunicar habilidades transferibles claramente
- Mostrar cómo experiencias previas aportan al rol objetivo
- Crear narrativa coherente de tu trayectoria

Resultado: Tu historia se transforma en valor, comunicando experiencia completa y aumentando significativamente tus chances de ser considerado, incluso sin experiencia directa en el rol objetivo.`,
      keyConcepts: [
        {
          name: 'Todos Tienen una Historia',
          description: 'Todos tienen una historia desde los 18 años en adelante. No descartes experiencias previas, transformalas en valor.',
          characteristics: [
            'Todos tienen historia desde los 18 años',
            'No descartar experiencias previas',
            'Transformar experiencias en valor',
            'Cada experiencia tiene algo que aportar',
            'Ejemplo: Técnico mecánico → habilidades de análisis, diseño, validación'
          ],
          examples: [
            'Técnico mecánico → diseñó prototipos, analizó conectores, hizo validaciones',
            'Cada experiencia previa tiene valor transferible',
            'No descartar porque "no es tecnología"'
          ]
        },
        {
          name: 'Transformar Experiencias en Valor',
          description: 'Experiencias que parecen no relacionadas pueden transformarse en habilidades valiosas para el rol que buscás.',
          characteristics: [
            'Analizar qué habilidades desarrollaste',
            'Transformar experiencias en valor',
            'Ejemplo: Técnico mecánico → diseño, análisis, validación, optimización',
            'Buscar habilidades transferibles',
            'No descartar porque no es tecnología'
          ],
          examples: [
            'Técnico mecánico: diseño de prototipos → diseño de sistemas',
            'Análisis de conectores → análisis de datos',
            'Validaciones y testeos → testing de software',
            'Optimización → optimización de procesos'
          ]
        },
        {
          name: 'No Descartar Experiencias "No Tecnológicas"',
          description: 'No digas "ah, no, todo eso no, porque no tiene nada que ver con tecnología". Busquemos la forma de transformarlo.',
          characteristics: [
            'No descartar experiencias no tecnológicas',
            'Buscar la forma de transformarlas',
            'Todas las experiencias tienen valor',
            'Soft skills son extremadamente demandadas',
            'Transformar en lugar de descartar'
          ],
          examples: [
            'Liderazgo de equipos → soft skills demandadas',
            'Coordinación de áreas → habilidades de gestión',
            'Experiencias no tecnológicas = soft skills valiosas'
          ]
        }
      ],
      bestPractices: [
        'No descartar experiencias previas',
        'Transformar experiencias en valor',
        'Buscar habilidades transferibles',
        'Identificar soft skills desarrolladas',
        'Usar historia personal para comunicar valor'
      ]
    }
  },
  {
    id: 16,
    level: 'AVANZADO',
    title: 'Experiencia Transferible',
    description: 'Aprendé cómo volcar experiencias laborales previas y retransformar conocimientos en algo que el mercado demanda.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 103, // "en función de eso, lo que es la experiencia transferible en lo que"
    content: {
      overview: `Todas tus experiencias laborales previas pueden retransformarse en valor para el mercado actual. La clave está en identificar qué conocimientos y habilidades desarrollaste que son relevantes y demandados hoy, incluso si provienen de contextos aparentemente no relacionados.

El proceso de retransformación implica analizar tus experiencias previas y extraer habilidades transferibles que el mercado demanda. Por ejemplo, una persona que lideró equipos en el área de deportes y coordinó cinco áreas con nueve personas a cargo, tiene habilidades valiosas: liderazgo, coordinación, gestión de equipos y áreas.

Estas habilidades son directamente transferibles y altamente demandadas, especialmente en un mundo técnico donde las soft skills son escasas. No se trata de descartar experiencias previas, sino de retransformarlas en valor tangible y útil para el mercado actual.`,
      why: `¿Por qué retransformar experiencias previas?

Porque maximiza tu valor y comunica experiencia completa. No necesitás empezar de cero, podés aprovechar todo lo que ya aprendiste.

El problema de descartar:
- Pérdida de valor: Descartar experiencias valiosas porque "no son del rol"
- Subestimación: No valorar conocimientos previos que tienen valor real
- Menos oportunidades: Perder chances por no mostrar experiencia completa
- Empezar de cero: No aprovechar lo que ya sabés

- Impacto: Retransformar experiencias previas te permite comunicar valor completo y aumentar significativamente tus chances, incluso sin experiencia directa en el rol objetivo.`,
      how: `¿Cómo retransformar experiencias previas en valor?

1. Analizar experiencias previas:
- Listar todas tus experiencias laborales
- Identificar qué conocimientos desarrollaste
- Buscar habilidades transferibles
- No descartar porque "no son tecnológicas"

2. Identificar qué demanda el mercado:
- Investigar qué habilidades busca el mercado
- Priorizar habilidades con alta demanda
- Enfocarse en soft skills (liderazgo, coordinación, gestión)
- Adaptar experiencias a demanda actual

3. Retransformar conocimientos:
- Ejemplo: Liderazgo de equipos → soft skill demandada
- Ejemplo: Coordinación de áreas → habilidades de gestión
- Ejemplo: Comunicación con áreas → habilidades interpersonales
- Transformar en lugar de descartar

4. Comunicar en el perfil:
- Incluir experiencias previas en "Sobre Mí"
- Destacar habilidades transferibles
- Mostrar cómo experiencias previas aportan valor
- Crear narrativa coherente

Resultado: Tus experiencias previas se retransforman en valor tangible y útil, comunicando experiencia completa y aumentando significativamente tus chances de ser considerado.`,
      keyConcepts: [
        {
          name: 'Retransformar Conocimientos Previos',
          description: 'Todas tus experiencias laborales previas tienen conocimientos que podés retransformar en algo que el mercado demanda hoy.',
          characteristics: [
            'Retransformar conocimientos previos',
            'Volcar experiencias como forma nueva',
            'Convertir en algo tangible y útil',
            'Adaptar a lo que el mercado demanda',
            'No perder valor de experiencias previas'
          ],
          examples: [
            'Experiencias previas → habilidades transferibles',
            'Conocimientos antiguos → valor actual',
            'Retransformar en lugar de descartar'
          ]
        },
        {
          name: 'Identificar Habilidades Transferibles',
          description: 'Identificá qué habilidades desarrollaste en experiencias previas que son transferibles al rol que buscás.',
          characteristics: [
            'Identificar habilidades desarrolladas',
            'Buscar qué es transferible',
            'Ejemplo: Liderazgo de equipos → soft skills',
            'Coordinación de áreas → habilidades de gestión',
            'Todas las experiencias tienen algo transferible'
          ],
          examples: [
            'Lideraste 9 personas en 5 áreas → liderazgo y coordinación',
            'Estas son habilidades transferibles valiosas',
            'Soft skills son extremadamente demandadas'
          ]
        },
        {
          name: 'Valor de Experiencias No Tecnológicas',
          description: 'Las experiencias no tecnológicas tienen mucho valor, especialmente las soft skills que son extremadamente demandadas.',
          characteristics: [
            'Soft skills son extremadamente demandadas',
            'Mundo técnico duro → soft skills valiosas',
            'No es tan fácil aprender soft skills',
            'Experiencias previas desarrollan soft skills',
            'No descartar porque no es tecnología'
          ],
          examples: [
            'Liderazgo de equipos → soft skill demandada',
            'Coordinación de áreas → habilidad valiosa',
            'Soft skills = valor oculto de experiencias previas'
          ]
        }
      ],
      bestPractices: [
        'Retransformar conocimientos previos en valor actual',
        'Identificar habilidades transferibles',
        'No descartar experiencias no tecnológicas',
        'Valorar soft skills desarrolladas',
        'Adaptar experiencias a lo que el mercado demanda'
      ]
    }
  },
  {
    id: 17,
    level: 'AVANZADO',
    title: 'Soft Skills: El Valor Oculto',
    description: 'Descubrí por qué las soft skills son extremadamente demandadas y cómo destacarlas en tu perfil.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1663, // "tan duro con la parte técnica, las soft skills son extremadamente demandadas"
    content: {
      overview: `En un mundo técnico donde las habilidades duras son comunes, las soft skills se convierten en el diferenciador crítico. Si ya lideraste equipos, coordinaste áreas o gestionaste proyectos, ya tenés soft skills valiosas que el mercado demanda intensamente.

El valor de las soft skills radica en su escasez: no es fácil aprenderlas, y muchas personas incluso sienten rechazo por desarrollarlas. Esto las convierte en habilidades extremadamente demandadas y bien valoradas. Tu historia personal, incluso si no es tecnológica, contiene un tesoro de soft skills que no debés descartar.

La estrategia no es eliminar experiencias porque "no tienen que ver con tecnología", sino identificar y transformar las soft skills que desarrollaste en valor comunicable para el mercado actual.`,
      why: `¿Por qué las soft skills son tan valiosas?

Porque son escasas y extremadamente demandadas. En un mundo técnico, las soft skills son el diferenciador crítico.

El problema de descartar soft skills:
- Pérdida de valor oculto: Descartar experiencias que desarrollaron soft skills valiosas
- Subestimación: No valorar habilidades que el mercado demanda intensamente
- Menos oportunidades: Perder chances por no mostrar soft skills
- Competitividad reducida: No destacar en un mercado saturado de habilidades técnicas

- Impacto: Las soft skills pueden ser tu mayor ventaja competitiva. Si ya las tenés, no las descartes; transformalas en valor comunicable.`,
      how: `¿Cómo identificar y destacar soft skills?

1. Identificar soft skills en tu historia:
- ¿Lideraste equipos? → Liderazgo
- ¿Coordinaste áreas? → Coordinación y gestión
- ¿Comunicaste con otras áreas? → Comunicación efectiva
- ¿Gestionaste proyectos? → Gestión y organización

2. Transformar experiencias en soft skills:
- Ejemplo: Lideraste 9 personas en 5 áreas → Liderazgo y coordinación
- Ejemplo: Área de deportes → Trabajo en equipo y disciplina
- Ejemplo: Comunicación con áreas → Habilidades interpersonales
- No descartar porque "no es tecnología"

3. Destacar en el perfil:
- Incluir soft skills en "Sobre Mí"
- Mencionar en descripciones de experiencia
- Mostrar cómo soft skills aportan valor
- Crear narrativa que destaque soft skills

4. Comunicar valor:
- Enfocarse en impacto, no solo en tareas
- Mostrar cómo soft skills generan resultados
- Conectar soft skills con valor para el negocio
- Evitar descartar experiencias valiosas

Resultado: Tus soft skills se convierten en tu diferenciador competitivo, comunicando valor único y aumentando significativamente tus chances de ser considerado.`,
      keyConcepts: [
        {
          name: 'Soft Skills Extremadamente Demandadas',
          description: 'En un mundo tan duro en la parte técnica, las soft skills son extremadamente demandadas porque no es tan fácil aprenderlas.',
          characteristics: [
            'Mundo técnico duro → soft skills valiosas',
            'No es tan fácil aprender soft skills',
            'Algunos sienten rechazo por aprenderlas',
            'Esto las hace más valiosas',
            'Extremadamente demandadas en el mercado'
          ],
          examples: [
            'Liderazgo de equipos → soft skill demandada',
            'Coordinación de áreas → habilidad valiosa',
            'Comunicación efectiva → muy demandada'
          ]
        },
        {
          name: 'Tu Historia Personal Tiene Valor',
          description: 'Tu historia personal, incluso si no es tecnológica, tiene mucho valor en forma de soft skills. No la descartes.',
          characteristics: [
            'Historia personal = valor en soft skills',
            'No descartar porque no es tecnología',
            'Buscar la forma de transformarlo',
            'Ejemplo: Lideraste equipos → ya sos líder',
            'Experiencias previas = soft skills desarrolladas'
          ],
          examples: [
            'Lideraste 9 personas en 5 áreas → ya sos líder',
            'Coordinaste áreas → habilidades de gestión',
            'Historia personal = soft skills valiosas'
          ]
        },
        {
          name: 'Transformar en Lugar de Descartar',
          description: 'No descartes experiencias porque no son tecnológicas. Buscá la forma de transformarlas en valor, especialmente soft skills.',
          characteristics: [
            'No descartar experiencias no tecnológicas',
            'Buscar la forma de transformarlas',
            'Transformar en soft skills',
            'Todas las experiencias tienen valor',
            'Ejemplo: Deportes → liderazgo, coordinación'
          ],
          examples: [
            'Área de deportes → liderazgo de equipos',
            'Coordinación de 5 áreas → habilidades de gestión',
            'Transformar en lugar de descartar'
          ]
        }
      ],
      bestPractices: [
        'Identificar soft skills en tu historia personal',
        'No descartar experiencias no tecnológicas',
        'Transformar experiencias en soft skills',
        'Destacar soft skills en tu perfil',
        'Entender que soft skills son extremadamente demandadas'
      ]
    }
  },
  {
    id: 18,
    level: 'AVANZADO',
    title: 'Benchmarking Exitoso',
    description: 'Aprendé cómo hacer benchmarking de perfiles exitosos para entender qué funciona y adaptarlo a tu perfil.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 180, // "también lo que es el benchmarking exitoso. Bueno, el benchmarking es ir a"
    content: {
      overview: `El benchmarking exitoso consiste en analizar perfiles de profesionales que lograron posiciones en empresas objetivo o roles similares al que buscás. Este proceso te permite entender qué funciona, qué estructura usan, y cómo comunican valor efectivamente.

La estrategia es buscar perfiles de personas que trabajan en empresas como Google, Amazon, o en roles similares al que buscás, y analizar cómo están estructurados sus perfiles. Si estas personas lograron entrar en esas empresas, es porque sus perfiles generan tracción y comunican valor efectivamente.

El benchmarking no se trata de copiar, sino de entender patrones exitosos y adaptarlos a tu perfil. Analizar títulos, estructura del "Sobre Mí", keywords utilizadas, y cómo comunican experiencia te da insights valiosos para optimizar tu propio perfil.`,
      why: `¿Por qué hacer benchmarking de perfiles exitosos?

Porque te permite aprender de lo que funciona. En lugar de adivinar, analizás perfiles que ya generan resultados.

El problema sin benchmarking:
- Adivinar: No saber qué funciona o qué no
- Ineficiencia: Perder tiempo probando cosas que no funcionan
- Oportunidades perdidas: No aprovechar patrones exitosos
- Competitividad reducida: No entender qué buscan los reclutadores

- Impacto: El benchmarking te da insights valiosos sobre qué funciona, permitiéndote optimizar tu perfil basándote en patrones exitosos en lugar de adivinar.`,
      how: `¿Cómo hacer benchmarking efectivo?

1. Identificar perfiles de referencia:
- Buscar personas que trabajan en empresas objetivo (Google, Amazon, etc.)
- Analizar perfiles de personas en roles similares al que buscás
- Identificar perfiles que generan tracción (muchas conexiones, actividad)
- Priorizar perfiles recientes y actualizados

2. Analizar estructura y contenido:
- ¿Cómo estructuran su título?
- ¿Qué keywords incluyen?
- ¿Cómo estructuran el "Sobre Mí"?
- ¿Cómo comunican experiencia y valor?

3. Identificar patrones exitosos:
- ¿Qué elementos comunes tienen los perfiles exitosos?
- ¿Qué estructura funciona mejor?
- ¿Qué keywords aparecen frecuentemente?
- ¿Cómo comunican valor efectivamente?

4. Adaptar a tu perfil:
- No copiar, adaptar patrones exitosos
- Aplicar estructura y keywords relevantes
- Mantener autenticidad y valor único
- Testear y ajustar según resultados

Resultado: Entendés qué funciona basándote en perfiles exitosos, permitiéndote optimizar tu perfil de forma más efectiva y aumentar tus chances de generar tracción.`,
      keyConcepts: [
        {
          name: 'Qué es Benchmarking de Perfiles',
          description: 'Benchmarking es ir a buscar perfiles exitosos de tu rol para entender cómo están armados y qué funciona.',
          characteristics: [
            'Buscar perfiles exitosos de tu rol',
            'Analizar cómo están armados',
            'Ver cómo están orientados',
            'Entender qué funciona',
            'Adaptar a tu propio perfil'
          ],
          examples: [
            'Buscar "Data Engineer" en LinkedIn',
            'Analizar perfiles que aparecen primero',
            'Ver estructura y keywords que usan'
          ]
        },
        {
          name: 'Referencias de Perfiles Exitosos',
          description: 'Tomar de referencia perfiles de personas de empresas top (Google, Amazon) porque si lograron entrar ahí, su perfil funciona.',
          characteristics: [
            'Perfiles de Google, Amazon, etc.',
            'Si lograron entrar ahí, su perfil funciona',
            'Generan o traccionan con su perfil',
            'Referencias confiables de éxito',
            'Adaptar elementos que funcionan'
          ],
          examples: [
            'Perfiles de Data Engineers en Google',
            'Perfiles de Data Engineers en Amazon',
            'Si entraron ahí, su perfil es efectivo'
          ]
        },
        {
          name: 'Qué Analizar en el Benchmarking',
          description: 'Analizá cómo están armados los perfiles exitosos: estructura, keywords, "Sobre Mí", experiencia, etc.',
          characteristics: [
            'Estructura del perfil',
            'Keywords que usan',
            'Cómo está escrito el "Sobre Mí"',
            'Cómo describen experiencia',
            'Qué elementos hacen que funcionen'
          ],
          examples: [
            'Analizar títulos efectivos',
            'Ver keywords top que usan',
            'Estudiar estructura del "Sobre Mí"',
            'Adaptar elementos que funcionan'
          ]
        }
      ],
      bestPractices: [
        'Buscar perfiles exitosos de tu rol',
        'Analizar estructura y contenido',
        'Tomar referencias de empresas top',
        'Adaptar elementos que funcionan',
        'No copiar, adaptar a tu situación'
      ]
    }
  },
  {
    id: 19,
    level: 'AVANZADO',
    title: 'Plan de Acción',
    description: 'Creá un plan de acción concreto para optimizar tu perfil de LinkedIn paso a paso.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 220, // "con su perfil de Link. Sí. Después, bueno, un plan de acción para que tengan"
    content: {
      overview: `Un plan de acción concreto te permite optimizar tu perfil de LinkedIn de forma sistemática y estructurada. En lugar de hacer cambios aleatorios, seguís pasos claros y ordenados que aseguran que no olvidás ningún elemento importante.

El plan de acción debe incluir todos los elementos clave: título optimizado, foto profesional, sección "Sobre Mí" efectiva, experiencia actualizada, networking estratégico, y keywords relevantes. Cada paso debe ser claro, concreto y accionable, permitiéndote implementarlo de forma sistemática.

La implementación paso a paso asegura que optimizás tu perfil completamente, sin dejar elementos importantes sin mejorar. Un plan bien estructurado te permite llevar el conocimiento a la acción, transformando insights en mejoras concretas en tu perfil.`,
      why: `¿Por qué necesitás un plan de acción?

Porque transforma conocimiento en acción. Sin un plan, los insights quedan como ideas sin implementar.

El problema sin plan:
- Inacción: Tener conocimiento pero no implementarlo
- Desorganización: Hacer cambios aleatorios sin estructura
- Elementos olvidados: Dejar partes importantes sin optimizar
- Ineficiencia: Perder tiempo sin resultados claros

- Impacto: Un plan de acción te permite implementar sistemáticamente todas las mejoras, asegurando que optimizás tu perfil completamente y maximizás tus resultados.`,
      how: `¿Cómo crear un plan de acción efectivo?

1. Identificar elementos clave:
- Título optimizado con keywords
- Foto profesional
- Sección "Sobre Mí" efectiva
- Experiencia actualizada
- Networking estratégico
- Keywords relevantes

2. Estructurar pasos claros:
- Paso 1: Optimizar título con keywords top demandadas
- Paso 2: Mejorar foto profesional (iluminación, sonrisa, vestimenta)
- Paso 3: Escribir "Sobre Mí" que refuerce rol y comunique valor
- Paso 4: Actualizar experiencia con habilidades transferibles
- Paso 5: Agregar contactos estratégicos (reclutadores, directores)

3. Priorizar tareas:
- Empezar con elementos de mayor impacto (título, foto)
- Continuar con contenido (Sobre Mí, experiencia)
- Finalizar con networking y optimización continua
- Revisar y ajustar según resultados

4. Implementar sistemáticamente:
- Seguir pasos en orden lógico
- Completar cada paso antes de continuar
- Testear efectividad de cada cambio
- Ajustar según feedback y resultados

Resultado: Tenés un plan claro y accionable que te permite optimizar tu perfil sistemáticamente, transformando conocimiento en mejoras concretas y maximizando tus resultados.`,
      keyConcepts: [
        {
          name: 'Estructura del Plan de Acción',
          description: 'Un plan de acción debe tener pasos claros y concretos que puedas seguir para optimizar tu perfil.',
          characteristics: [
            'Pasos claros y concretos',
            'Orden lógico de implementación',
            'Checklist de elementos a optimizar',
            'Priorización de tareas',
            'Timeline de implementación'
          ],
          examples: [
            'Paso 1: Optimizar título con keywords',
            'Paso 2: Mejorar foto profesional',
            'Paso 3: Escribir "Sobre Mí" efectivo',
            'Paso 4: Actualizar experiencia',
            'Paso 5: Agregar contactos estratégicos'
          ]
        },
        {
          name: 'Elementos Clave del Plan',
          description: 'El plan debe cubrir todos los elementos clave: título, foto, "Sobre Mí", experiencia, networking, etc.',
          characteristics: [
            'Título optimizado con keywords',
            'Foto profesional',
            '"Sobre Mí" que comunica valor',
            'Experiencia actualizada',
            'Networking estratégico',
            'Benchmarking realizado'
          ],
          examples: [
            'Checklist completo de optimización',
            'Todos los elementos importantes cubiertos',
            'Plan sistemático y completo'
          ]
        },
        {
          name: 'Implementación y Seguimiento',
          description: 'El plan debe ser implementable y tener forma de hacer seguimiento del progreso.',
          characteristics: [
            'Implementación paso a paso',
            'Seguimiento del progreso',
            'Ajustes según resultados',
            'Medición de efectividad',
            'Iteración y mejora continua'
          ],
          examples: [
            'Implementar un paso a la vez',
            'Medir resultados (ofertas recibidas)',
            'Ajustar según feedback'
          ]
        }
      ],
      bestPractices: [
        'Crear plan de acción concreto y claro',
        'Priorizar elementos más importantes primero',
        'Implementar paso a paso',
        'Hacer seguimiento del progreso',
        'Ajustar plan según resultados'
      ]
    }
  },
  {
    id: 20,
    level: 'AVANZADO',
    title: 'El Guión: Contenido Completo del Perfil',
    description: 'Aprendé cómo estructurar el contenido completo de tu perfil después del hook para desarrollar tu valor.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1130, // "el hook, que es lo que viene acá, lo que es la foto perfil y las primeras"
    content: {
      overview: `El guión es la parte del perfil que desarrolla tu valor después de que el hook captó la atención del reclutador. Mientras que el hook tiene 3-5 segundos para generar interés, el guión tiene el espacio completo para comunicar tu experiencia, proyectos, recomendaciones y habilidades.

La estructura del guión incluye todos los elementos que suman y aportan: experiencia laboral detallada, proyectos destacados, recomendaciones de colegas y managers, habilidades técnicas y soft skills, educación y certificaciones, y logros relevantes. Cada elemento refuerza tu propuesta de valor y desarrolla el mensaje inicial del hook.

Un perfil efectivo necesita tanto el hook como el guión. El hook sin guión es una oportunidad perdida: captaste atención pero no desarrollaste valor. El guión sin hook nunca se lee: tenés valor pero nadie lo ve porque no captaste atención inicial.`,
      why: `¿Por qué el guión es crítico?

Porque desarrolla el valor que el hook prometió. Sin guión efectivo, perdés la oportunidad después de captar atención.

El problema sin guión efectivo:
- Oportunidad perdida: Captaste atención pero no desarrollaste valor
- Mensaje incompleto: El reclutador no entiende tu valor completo
- Menos oportunidades: No aprovechás la atención captada
- Hook desperdiciado: Invertiste en hook pero no en desarrollar valor

- Impacto: Un guión efectivo transforma la atención captada por el hook en interés genuino y oportunidades concretas, mientras que un guión débil desperdicia la atención inicial.`,
      how: `¿Cómo estructurar el guión efectivamente?

1. Incluir elementos clave:
- Experiencia laboral con descripciones detalladas
- Proyectos destacados (personales o profesionales)
- Recomendaciones de colegas y managers
- Habilidades técnicas y soft skills
- Educación y certificaciones relevantes
- Logros y reconocimientos

2. Desarrollar valor prometido:
- Si el hook menciona tecnologías, el guión las desarrolla
- Si el hook menciona experiencia, el guión la detalla
- Si el hook menciona resultados, el guión los cuantifica
- Mantener consistencia con el mensaje del hook

3. Estructurar por importancia:
- Empezar con experiencia más relevante
- Continuar con proyectos destacados
- Incluir recomendaciones que validen tu valor
- Finalizar con habilidades y educación

4. Comunicar impacto, no solo tareas:
- Enfocarse en resultados e impacto
- Usar números y métricas cuando sea posible
- Mostrar cómo generaste valor para el negocio
- Evitar listas de tareas sin contexto

Resultado: Tu guión desarrolla completamente el valor prometido en el hook, transformando atención inicial en interés genuino y oportunidades concretas.`,
      keyConcepts: [
        {
          name: 'Guión: Desarrollar Valor Después del Hook',
          description: 'El guión es la parte del perfil que desarrolla tu valor después de que el hook captó la atención del reclutador.',
          characteristics: [
            'Desarrolla valor después del hook',
            'Experiencia como ingeniero de datos',
            'Experiencia en trabajos anteriores',
            'Proyectos que armaste',
            'Recomendaciones que tenés',
            'Habilidades que dominás'
          ],
          examples: [
            'Experiencia laboral detallada',
            'Proyectos destacados',
            'Recomendaciones de colegas',
            'Habilidades técnicas y soft skills'
          ]
        },
        {
          name: 'Elementos del Guión',
          description: 'El guión incluye todos los elementos que suman y aportan: experiencia, proyectos, habilidades, recomendaciones.',
          characteristics: [
            'Experiencia laboral',
            'Proyectos destacados',
            'Recomendaciones',
            'Habilidades técnicas',
            'Educación y certificaciones',
            'Logros y reconocimientos'
          ],
          examples: [
            'Experiencia: trabajos anteriores con descripciones',
            'Proyectos: proyectos personales o profesionales',
            'Recomendaciones: de colegas y managers',
            'Habilidades: técnicas y soft skills'
          ]
        },
        {
          name: 'Hook + Guión = Perfil Completo',
          description: 'Un perfil efectivo necesita tanto el hook (que capta atención) como el guión (que desarrolla valor).',
          characteristics: [
            'Hook capta atención (3-5 seg)',
            'Guión desarrolla valor (resto del perfil)',
            'Ambos son necesarios',
            'Hook sin guión = oportunidad perdida',
            'Guión sin hook = nunca se lee'
          ],
          examples: [
            'Hook efectivo + guión completo = perfil optimizado',
            'Ambos trabajan juntos',
            'Estructura completa y efectiva'
          ]
        }
      ],
      bestPractices: [
        'Desarrollar guión completo después del hook',
        'Incluir experiencia, proyectos, recomendaciones',
        'Destacar habilidades relevantes',
        'Mantener consistencia con el hook',
        'Asegurarse de que el guión desarrolle el valor prometido en el hook'
      ]
    }
  },
  {
    id: 21,
    level: 'AVANZADO',
    title: 'Comunicar Valor vs Mostrar Conocimiento',
    description: 'Aprendé la diferencia crítica entre comunicar valor efectivamente y solo mostrar conocimiento.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 621, // "oportunidades por una falta de comunicar su valor o por no saber hacerlo. E eso"
    content: {
      overview: `La diferencia entre profesionales exitosos y aquellos con menos oportunidades no siempre está en el conocimiento, sino en la capacidad de comunicar valor efectivamente. Muchas personas con conocimiento profundo no logran los mismos sueldos o oportunidades porque no saben cómo comunicar su valor.

Esta diferencia crítica impacta directamente en tu carrera. Comunicar valor es tan importante como tenerlo. Una persona con conocimiento medio pero que comunica bien su valor puede tener más oportunidades que alguien con conocimiento profundo pero que no sabe comunicarlo.

Tu perfil de LinkedIn es la herramienta principal para comunicar valor, no solo para mostrar conocimiento. Un perfil optimizado comunica tu valor automáticamente, mientras que uno mal estructurado oculta tu verdadero potencial, incluso si tenés la experiencia perfecta.`,
      why: `¿Por qué comunicar valor es crítico?

Porque determina las oportunidades que recibís y los sueldos que podés negociar. El conocimiento sin comunicación efectiva tiene impacto limitado.

El problema de no comunicar valor:
- Oportunidades perdidas: Conocimiento profundo pero mal comunicado = menos oportunidades
- Sueldos más bajos: No comunicar valor = menos poder de negociación
- Subestimación: Tu verdadero potencial queda oculto
- Competitividad reducida: Otros con menos conocimiento pero mejor comunicación te superan

- Impacto: Comunicar valor efectivamente puede multiplicar tus oportunidades y sueldos, mientras que no comunicarlo limita significativamente tu potencial de carrera.`,
      how: `¿Cómo comunicar valor efectivamente?

1. Enfocarse en impacto, no solo en tareas:
- En lugar de "Trabajé con Python", "Desarrollé pipelines que procesaron 10M registros/día"
- En lugar de "Gestioné proyectos", "Lideré proyecto que redujo costos 30%"
- Usar números y métricas cuando sea posible
- Mostrar cómo generaste valor para el negocio

2. Usar el perfil como herramienta de comunicación:
- Keywords estratégicas que comuniquen expertise
- Estructura clara que facilite lectura
- Contenido que destaque valor único
- Consistencia entre título, "Sobre Mí" y experiencia

3. Comunicar valor único:
- ¿Qué te diferencia de otros candidatos?
- ¿Qué problemas resolviste que otros no?
- ¿Qué resultados generaste que sean destacables?
- Enfocarse en diferenciadores, no en lo genérico

4. Optimizar continuamente:
- Revisar y ajustar según feedback
- Testear diferentes formas de comunicar valor
- Ajustar según resultados (ofertas recibidas)
- Iterar y mejorar comunicación constantemente

Resultado: Comunicás tu valor efectivamente, aumentando significativamente tus oportunidades y poder de negociación, incluso si otros tienen más conocimiento pero no saben comunicarlo.`,
      keyConcepts: [
        {
          name: 'Comunicar Valor vs Tener Conocimiento',
          description: 'Comunicar valor efectivamente es tan importante como tener conocimiento. Mucha gente sabe más pero no comunica bien.',
          characteristics: [
            'Comunicar valor = tan importante como tenerlo',
            'Mucha gente sabe más pero no comunica bien',
            'Menos oportunidades por no comunicar valor',
            'Importa tanto lo que sabés como cómo lo comunicás',
            'Comunicar valor efectivamente marca la diferencia'
          ],
          examples: [
            'Persona con mucho conocimiento pero mal comunicado = menos oportunidades',
            'Persona con conocimiento medio pero bien comunicado = más oportunidades',
            'Comunicar valor es la clave'
          ]
        },
        {
          name: 'El Perfil como Herramienta de Comunicación',
          description: 'Tu perfil de LinkedIn es la herramienta para comunicar tu valor, no solo para mostrar conocimiento.',
          characteristics: [
            'Perfil = herramienta de comunicación',
            'Comunica valor, no solo conocimiento',
            'Optimización = mejor comunicación',
            'Perfil optimizado comunica valor automáticamente',
            'Herramienta para destacar tu valor único'
          ],
          examples: [
            'Perfil optimizado comunica valor sin esfuerzo',
            'Keywords, estructura, contenido = comunicación efectiva',
            'Herramienta para hacer que tu valor sea visible'
          ]
        },
        {
          name: 'Impacto en Oportunidades y Sueldos',
          description: 'Comunicar valor efectivamente impacta directamente en las oportunidades que recibís y los sueldos que podés negociar.',
          characteristics: [
            'Comunicar valor = más oportunidades',
            'Comunicar valor = mejores sueldos',
            'No comunicar valor = menos oportunidades',
            'Diferencia en resultados por comunicación',
            'Impacto directo en carrera'
          ],
          examples: [
            'Misma habilidad, diferente comunicación = diferentes resultados',
            'Comunicar valor = más ofertas y mejores sueldos',
            'Impacto real en oportunidades de carrera'
          ]
        }
      ],
      bestPractices: [
        'Enfocarse en comunicar valor, no solo mostrar conocimiento',
        'Usar el perfil como herramienta de comunicación',
        'Optimizar para comunicar valor efectivamente',
        'Entender que comunicación = oportunidades',
        'Invertir tiempo en comunicar bien tu valor'
      ]
    }
  },
  {
    id: 22,
    level: 'AVANZADO',
    title: 'LinkedIn vs CV',
    description: 'Comprendé por qué LinkedIn es más que un CV y cómo aprovechar sus ventajas sobre un CV tradicional.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 7, // "tienen y ya saben de su perfil de LinkedIn para poder hacerlo trabajar por"
    content: {
      overview: `LinkedIn es significativamente más que un CV tradicional. Es el primer punto de contacto donde los reclutadores y profesionales te encuentran, incluso antes de que envíes tu CV. Esta posición privilegiada lo convierte en tu herramienta más importante para generar oportunidades.

A diferencia de un CV estático que solo se ve cuando lo enviás, LinkedIn es dinámico, visible para todos, y permite networking activo. Los reclutadores buscan candidatos en LinkedIn primero, y tu perfil es su primera impresión de vos. Para cargos directivos o roles que requieren transmitir confianza, LinkedIn debe comunicar claramente tu valor y credibilidad.

La ventaja de LinkedIn sobre un CV es que trabaja 24/7 en tu favor. Mientras que un CV solo funciona cuando lo enviás, LinkedIn genera oportunidades automáticamente cuando está optimizado correctamente.`,
      why: `¿Por qué LinkedIn es más importante que un CV?

Porque es el primer punto de contacto y trabaja continuamente. Los reclutadores buscan en LinkedIn antes de ver CVs.

Ventajas de LinkedIn sobre CV:
- Primer punto de contacto: Reclutadores te encuentran antes de que envíes CV
- Dinámico: Se actualiza fácilmente, no necesitás enviarlo cada vez
- Visible: Todos pueden verlo sin que envíes nada
- Networking activo: Permite conectar y generar oportunidades
- Trabaja 24/7: Genera oportunidades automáticamente cuando está optimizado

- El problema de depender solo del CV: Un CV solo funciona cuando lo enviás. LinkedIn trabaja continuamente, generando oportunidades incluso cuando no estás buscando activamente.`,
      how: `¿Cómo aprovechar LinkedIn sobre un CV?

1. Optimizar como primer punto de contacto:
- Asegurar que tu perfil comunique valor inmediatamente
- Incluir keywords estratégicas para aparecer en búsquedas
- Estructurar para que sea fácil de escanear
- Transmitir confianza y profesionalismo

2. Aprovechar ventajas dinámicas:
- Actualizar fácilmente según tendencias del mercado
- Agregar nuevas experiencias y proyectos sin reenviar
- Ajustar keywords según demanda actual
- Mantener perfil siempre actualizado

3. Usar networking activo:
- Conectar con reclutadores y profesionales clave
- Participar en grupos y comunidades relevantes
- Compartir contenido que demuestre expertise
- Generar oportunidades a través de red

4. Hacer que trabaje 24/7:
- Optimizar para que algoritmos te encuentren
- Configurar alertas personalizadas
- Mantener perfil actualizado y profesional
- Dejar que LinkedIn genere oportunidades automáticamente

Resultado: LinkedIn se convierte en tu herramienta principal, trabajando continuamente para generar oportunidades, mientras que un CV solo funciona cuando lo enviás activamente.`,
      keyConcepts: [
        {
          name: 'LinkedIn es el Primer Punto de Contacto',
          description: 'LinkedIn es por el primer lugar por el que van a entrar todos los reclutadores o las personas que nos conozcan.',
          characteristics: [
            'Primer lugar donde te encuentran',
            'Antes que el CV',
            'Primera impresión',
            'Punto de entrada para reclutadores',
            'Más importante que el CV en muchos casos'
          ],
          examples: [
            'Reclutadores buscan en LinkedIn primero',
            'LinkedIn antes que CV en proceso de selección',
            'Primera impresión en LinkedIn'
          ]
        },
        {
          name: 'LinkedIn es Dinámico y Visible',
          description: 'A diferencia de un CV estático, LinkedIn es dinámico, visible para todos, y permite networking activo.',
          characteristics: [
            'Dinámico: se actualiza fácilmente',
            'Visible: todos pueden verlo',
            'Permite networking activo',
            'Alertas automáticas de oportunidades',
            'Más funcional que un CV estático'
          ],
          examples: [
            'Actualización fácil vs CV que hay que enviar',
            'Visible para reclutadores sin enviar nada',
            'Networking que un CV no permite'
          ]
        },
        {
          name: 'Transmitir Confianza y Claridad',
          description: 'LinkedIn debe transmitir confianza y claridad, especialmente para cargos directivos o cuando necesitás transmitir confianza a clientes.',
          characteristics: [
            'Debe transmitir confianza',
            'Debe ser claro',
            'Especialmente importante para cargos directivos',
            'Transmite confianza a clientes',
            'Más que un CV: herramienta de marca personal'
          ],
          examples: [
            'Cargos directivos: LinkedIn transmite confianza',
            'Clientes potenciales ven tu LinkedIn',
            'Herramienta de marca personal'
          ]
        }
      ],
      bestPractices: [
        'Optimizar LinkedIn como primer punto de contacto',
        'Entender que es más importante que el CV en muchos casos',
        'Mantenerlo actualizado y dinámico',
        'Transmitir confianza y claridad',
        'Usar LinkedIn como herramienta de marca personal'
      ]
    }
  },
  {
    id: 23,
    level: 'AVANZADO',
    title: 'Casos de Estudio y Ejemplos Prácticos',
    description: 'Analizá casos de estudio reales de búsquedas de reclutadores y cómo diferentes perfiles aparecen o quedan fuera.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 828, // "perfil propio, es lo primero que va a llamar o no la atención del reclutador."
    content: {
      overview: `Los casos de estudio reales de búsquedas de reclutadores revelan patrones claros sobre qué perfiles funcionan y cuáles quedan fuera. Cuando un reclutador busca "Data Engineer", aparecen perfiles con estructuras similares: rol específico seguido de keywords tecnológicas relevantes.

El análisis de resultados muestra que los perfiles efectivos tienen características comunes: incluyen keywords específicas (Databricks, SQL, Python, Azure), son claros sobre el rol, y comunican valor inmediatamente. Los perfiles que no funcionan carecen de keywords, son confusos, o no comunican valor claramente.

Estos casos prácticos demuestran la importancia crítica de tener keywords estratégicas y claridad en el perfil. Un perfil sin keywords relevantes queda fuera de búsquedas, incluso si la persona tiene la experiencia perfecta.`,
      why: `¿Por qué analizar casos de estudio reales?

Porque muestran qué funciona en la práctica. En lugar de teorizar, ves resultados reales de búsquedas de reclutadores.

El valor de casos prácticos:
- Resultados reales: Ves qué perfiles aparecen y cuáles no
- Patrones claros: Identificás qué elementos funcionan consistentemente
- Aprendizaje concreto: Entendés por qué algunos perfiles funcionan y otros no
- Aplicación directa: Podés adaptar lo que funciona a tu perfil

- Impacto: Analizar casos reales te da insights prácticos sobre qué funciona, permitiéndote optimizar tu perfil basándote en resultados reales, no en suposiciones.`,
      how: `¿Cómo usar casos de estudio para optimizar tu perfil?

1. Analizar búsquedas reales:
- Buscar tu rol objetivo en LinkedIn
- Ver qué perfiles aparecen primero
- Identificar patrones comunes en perfiles exitosos
- Notar qué keywords y estructura usan

2. Identificar qué funciona:
- Keywords específicas que aparecen frecuentemente
- Estructura de títulos que funciona
- Cómo comunican valor efectivamente
- Elementos comunes en perfiles exitosos

3. Identificar qué no funciona:
- Perfiles sin keywords relevantes
- Títulos confusos o ambiguos
- Falta de claridad en el rol
- Elementos que hacen que perfiles queden fuera

4. Adaptar a tu perfil:
- Incluir keywords que funcionan en tu área
- Usar estructura similar a perfiles exitosos
- Evitar errores que hacen que perfiles queden fuera
- Testear y ajustar según resultados

Resultado: Optimizás tu perfil basándote en casos reales de lo que funciona, aumentando significativamente tus chances de aparecer en búsquedas relevantes y ser considerado por reclutadores.`,
      keyConcepts: [
        {
          name: 'Análisis de Búsquedas Reales',
          description: 'Analizar búsquedas reales de reclutadores muestra qué perfiles aparecen y cuáles quedan fuera, y por qué.',
          characteristics: [
            'Búsquedas reales muestran qué funciona',
            'Algunos perfiles aparecen, otros no',
            'Keywords determinan visibilidad',
            'Claridad determina consideración',
            'Análisis práctico de resultados'
          ],
          examples: [
            'Búsqueda: "Data Engineer"',
            'Resultados: perfiles con keywords específicas',
            'Análisis de qué hace que aparezcan'
          ]
        },
        {
          name: 'Perfiles que Funcionan',
          description: 'Los perfiles que funcionan tienen keywords específicas, son claros, y comunican valor inmediatamente.',
          characteristics: [
            'Keywords específicas (Databricks, SQL, Python)',
            'Claridad en el rol',
            'Comunican valor inmediatamente',
            'Aparecen en búsquedas relevantes',
            'Ejemplo: "Senior Data Engineer, Azure, Databricks, SQL, Python"'
          ],
          examples: [
            '✅ "Data Engineer, Databricks, SQL, Python" → Perfecto',
            '✅ "Senior Data Engineer, Azure, Databricks, SQL, Python" → Además es Senior',
            'Perfiles claros y específicos funcionan'
          ]
        },
        {
          name: 'Perfiles que No Funcionan',
          description: 'Los perfiles que no funcionan carecen de keywords, son confusos, o no comunican valor claramente.',
          characteristics: [
            'Sin keywords específicas',
            'Confusos o ambiguos',
            'No comunican valor',
            'Quedan fuera de búsquedas',
            'Ejemplo: "Data Engineer, Maestría en Data Science" (sin keywords)'
          ],
          examples: [
            '❌ "Data Engineer, Maestría en Data Science" → No dice si sabe Python, SQL, etc.',
            '❌ "Data Manager, Data Engineer" → Confuso, no específico',
            'Perfiles sin keywords o confusos no funcionan'
          ]
        }
      ],
      bestPractices: [
        'Analizar búsquedas reales para entender qué funciona',
        'Aprender de perfiles que aparecen primero',
        'Evitar errores de perfiles que no funcionan',
        'Aplicar aprendizajes a tu propio perfil',
        'Iterar basándote en casos de estudio reales'
      ]
    },
    examples: [
      {
        id: 1,
        title: 'Análisis de Búsqueda Real',
        description: 'Caso de estudio de cómo un reclutador evalúa perfiles en una búsqueda',
        scenario: `Búsqueda del Reclutador:
"Data Engineer"

Perfiles que Aparecen:
1. "Data Engineer, Azure, Databricks, SQL, Data Factory" → ✅ Aparece
2. "Data Engineer, Maestría en Data Science" → ❌ No dice keywords
3. "Data Manager, Data Engineer" → ❌ Confuso, no específico
4. "Data Engineer, Databricks, SQL, Python" → ✅ Perfecto, lo tomo
5. "Senior Data Engineer, Azure, Databricks, SQL, Python" → ✅ Además es Senior, listo

Análisis:
- Perfiles 1, 4, 5 aparecen porque tienen keywords específicas
- Perfil 2 no aparece porque falta keywords (aunque tiene maestría)
- Perfil 3 no aparece porque es confuso (múltiples roles)`,
        solution: `Los perfiles que funcionan tienen keywords específicas y son claros. Los que no tienen keywords o son confusos quedan fuera, incluso si tienen credenciales académicas. Keywords + claridad = visibilidad y oportunidades.`
      }
    ]
  },
  {
    id: 24,
    level: 'AVANZADO',
    title: 'Preguntas Frecuentes',
    description: 'Resolvé dudas comunes sobre optimización de LinkedIn, como si mayúsculas/minúsculas afectan las búsquedas.',
    difficulty: 'advanced',
    // videoTimestamp removido - usar videoUrl opcional si hay charla completa relacionada
    // videoTimestamp: 1068, // "demanda no es tal para esos perfiles. En cambio, ayudar SQL, Python y bueno, son"
    content: {
      overview: `Las preguntas frecuentes sobre optimización de LinkedIn surgen naturalmente durante el proceso. Resolver estas dudas es crítico para optimizar correctamente y evitar errores comunes que pueden limitar tu efectividad.

Una pregunta común es si las mayúsculas o minúsculas afectan las búsquedas. La respuesta es que LinkedIn es case insensitive: no importa si escribís las keywords en mayúsculas o minúsculas, mientras sea la palabra clave correcta, funcionará igual en las búsquedas.

Otras preguntas frecuentes incluyen: ¿cuántas keywords incluir? ¿Cómo actualizar el perfil regularmente? ¿Cuándo cambiar el título? ¿Cómo medir efectividad? Resolver estas dudas te permite optimizar con confianza y evitar errores que pueden limitar tus resultados.`,
      why: `¿Por qué resolver preguntas frecuentes es importante?

Porque elimina dudas que pueden limitar tu optimización. Sin respuestas claras, podés cometer errores o no optimizar completamente.

El problema de dudas sin resolver:
- Incertidumbre: No saber si estás optimizando correctamente
- Errores evitables: Cometer errores por falta de claridad
- Optimización incompleta: No optimizar completamente por dudas
- Oportunidades perdidas: Perder chances por no resolver dudas

- Impacto: Resolver preguntas frecuentes te permite optimizar con confianza, evitando errores y maximizando tus resultados.`,
      how: `¿Cómo resolver preguntas frecuentes efectivamente?

1. Identificar dudas comunes:
- ¿Mayúsculas/minúsculas afectan búsquedas? (No, case insensitive)
- ¿Cuántas keywords incluir? (3-5 principales en título)
- ¿Cómo actualizar regularmente? (Según tendencias del mercado)
- ¿Cuándo cambiar el título? (Cuando cambia tu rol objetivo o keywords)
- ¿Cómo medir efectividad? (Ofertas recibidas, contactos de reclutadores)

2. Buscar respuestas confiables:
- Consultar con reclutadores o profesionales experimentados
- Analizar casos de estudio reales
- Probar y medir resultados
- Ajustar según feedback

3. Aplicar respuestas a tu optimización:
- Usar información para optimizar correctamente
- Evitar errores comunes identificados
- Optimizar completamente sin dudas
- Testear y ajustar según resultados

4. Iterar y mejorar:
- Resolver nuevas dudas que surjan
- Ajustar optimización según aprendizajes
- Compartir conocimiento con otros
- Mejorar continuamente

Resultado: Resolvés dudas comunes, optimizás con confianza, y maximizás tus resultados sin errores evitables.`,
      keyConcepts: [
        {
          name: 'Mayúsculas vs Minúsculas en Keywords',
          description: 'Las búsquedas de LinkedIn son case insensitive. No importa si escribís las keywords en mayúsculas o minúsculas.',
          characteristics: [
            'Case insensitive: mayúsculas/minúsculas no importan',
            'Mientras sea la palabra clave, funciona',
            'No afecta las búsquedas',
            'Puedes escribir como prefieras',
            'Lo importante es que esté la keyword'
          ],
          examples: [
            '"Data Engineer" = "data engineer" = "DATA ENGINEER"',
            'Todas funcionan igual en búsquedas',
            'Lo importante es que esté la palabra clave'
          ]
        },
        {
          name: 'Otras Preguntas Frecuentes',
          description: 'Hay otras preguntas comunes sobre optimización de LinkedIn que es importante resolver.',
          characteristics: [
            '¿Cuántas keywords incluir?',
            '¿Cómo actualizar el perfil regularmente?',
            '¿Cuándo cambiar el título?',
            '¿Cómo medir efectividad?',
            'Preguntas sobre networking y contactos'
          ],
          examples: [
            'Preguntas sobre frecuencia de actualización',
            'Preguntas sobre cantidad de keywords',
            'Preguntas sobre medición de resultados'
          ]
        },
        {
          name: 'Importancia de Resolver Dudas',
          description: 'Resolver dudas comunes ayuda a optimizar correctamente y evitar errores que reduzcan oportunidades.',
          characteristics: [
            'Resolver dudas = optimización correcta',
            'Evitar errores comunes',
            'Maximizar oportunidades',
            'Asegurar implementación efectiva',
            'Confianza en la optimización'
          ],
          examples: [
            'Saber que mayúsculas/minúsculas no importan',
            'Evitar preocupaciones innecesarias',
            'Enfocarse en lo que realmente importa'
          ]
        }
      ],
      bestPractices: [
        'No preocuparse por mayúsculas/minúsculas en keywords',
        'Enfocarse en incluir las keywords correctas',
        'Resolver dudas antes de optimizar',
        'Buscar respuestas a preguntas comunes',
        'No dejar que dudas bloqueen la optimización'
      ]
    }
  },
  {
    id: 25,
    level: 'AVANZADO',
    title: 'Resumen y Próximos Pasos',
    description: 'Resumen completo de todos los conceptos y plan de acción para implementar la optimización de tu perfil.',
    difficulty: 'advanced',
    content: {
      overview: `Este resumen integra todos los conceptos clave aprendidos y proporciona un plan de acción concreto para optimizar tu perfil de LinkedIn. Los conceptos fundamentales incluyen: LinkedIn como sistema automatizado que trabaja 24/7, el hook que capta atención en 3-5 segundos, palabras clave estratégicas que determinan visibilidad, claridad y especialización, comunicación de valor efectiva, experiencia transferible y soft skills, y benchmarking de perfiles exitosos.

La implementación requiere crear un plan de acción personalizado basado en tu situación específica y el rol que buscás. Este plan debe incluir pasos claros y concretos para optimizar cada elemento de tu perfil, desde el título hasta el networking, asegurando que cada cambio contribuya a multiplicar tus oportunidades.

El siguiente paso es la acción: evaluar tu perfil actual, identificar áreas de mejora, y comenzar a implementar las optimizaciones de forma sistemática. El conocimiento sin implementación no genera resultados; es momento de transformar insights en mejoras concretas.`,
      why: `¿Por qué necesitás un resumen y plan de acción?

Porque integra todo lo aprendido y te da pasos concretos para implementar. Sin un plan, el conocimiento queda como teoría sin aplicación.

El problema sin plan:
- Conocimiento sin acción: Tener insights pero no implementarlos
- Desorganización: No saber por dónde empezar
- Optimización incompleta: Dejar elementos importantes sin mejorar
- Resultados limitados: No maximizar el potencial de optimización

- Impacto: Un resumen y plan de acción te permite implementar sistemáticamente todas las optimizaciones, transformando conocimiento en resultados concretos y multiplicando tus oportunidades.`,
      how: `¿Cómo crear e implementar tu plan de acción?

1. Evaluar tu perfil actual:
- Analizar título, foto, "Sobre Mí", experiencia
- Identificar qué elementos necesitan optimización
- Comparar con perfiles exitosos (benchmarking)
- Listar áreas de mejora prioritarias

2. Crear plan personalizado:
- Paso 1: Optimizar título con keywords top demandadas
- Paso 2: Mejorar foto profesional
- Paso 3: Escribir "Sobre Mí" que refuerce rol
- Paso 4: Actualizar experiencia con valor transferible
- Paso 5: Agregar contactos estratégicos

3. Implementar sistemáticamente:
- Seguir pasos en orden lógico
- Completar cada paso antes de continuar
- Testear efectividad de cada cambio
- Ajustar según feedback y resultados

4. Medir y iterar:
- Medir resultados (ofertas recibidas, contactos)
- Ajustar optimización según resultados
- Iterar y mejorar continuamente
- Mantener perfil actualizado según tendencias

Resultado: Tenés un plan claro y accionable que te permite optimizar tu perfil sistemáticamente, transformando conocimiento en mejoras concretas y multiplicando tus oportunidades de forma sostenible.`,
      keyConcepts: [
        {
          name: 'Resumen de Conceptos Clave',
          description: 'Todos los conceptos clave que aprendiste: LinkedIn como sistema, hook, keywords, claridad, comunicación de valor, etc.',
          characteristics: [
            'LinkedIn como sistema automatizado',
            'El hook: primeros 3-5 segundos',
            'Palabras clave estratégicas',
            'Claridad: menos es más',
            'Comunicar valor vs mostrar conocimiento',
            'Experiencia transferible y soft skills',
            'Benchmarking exitoso'
          ],
          examples: [
            'Todos los conceptos integrados',
            'Visión completa de optimización',
            'Entendimiento holístico del proceso'
          ]
        },
        {
          name: 'Plan de Acción Personalizado',
          description: 'Crear tu plan de acción personalizado basado en tu situación específica y el rol que buscás.',
          characteristics: [
            'Evaluar tu perfil actual',
            'Identificar áreas de mejora',
            'Priorizar acciones',
            'Implementar paso a paso',
            'Medir resultados'
          ],
          examples: [
            'Checklist personalizado de optimización',
            'Pasos específicos para tu situación',
            'Timeline de implementación'
          ]
        },
        {
          name: 'Próximos Pasos Concretos',
          description: 'Pasos concretos e inmediatos que podés tomar para empezar a optimizar tu perfil hoy mismo.',
          characteristics: [
            'Optimizar título con keywords',
            'Mejorar foto profesional',
            'Escribir "Sobre Mí" efectivo',
            'Actualizar experiencia',
            'Agregar contactos estratégicos',
            'Hacer benchmarking'
          ],
          examples: [
            'Acciones inmediatas que podés tomar',
            'Pasos concretos y accionables',
            'Empezar hoy mismo'
          ]
        }
      ],
      bestPractices: [
        'Revisar todos los conceptos aprendidos',
        'Crear plan de acción personalizado',
        'Empezar con acciones inmediatas',
        'Implementar paso a paso',
        'Medir resultados y ajustar',
        'Iterar y mejorar continuamente'
      ]
    }
  }
];

